/**
 * Weapon and destruction effects: tracers, muzzle flashes, explosions, fire.
 *
 * Everything here is **presentation**. It reads the simulation's event
 * vocabulary and its own clock, and writes nothing back — a particle that
 * influenced the simulation would be a desync, since particle counts are capped
 * by a quality preset that differs between machines.
 *
 * Three `InstancedMesh`es for the lot. A particle system that allocates a mesh
 * per burst produces a draw call per burst, and draw calls are the binding
 * constraint at this resolution.
 *
 * ---
 *
 * **The three things this file exists to get right, learned the hard way.**
 *
 * 1. *A burst is a size distribution, not a count.* Spawning N identical cubes
 *    produces a cotton ball with a hard convex outline however many of them
 *    there are. The reference look is a handful of large cubes in the core and
 *    a long small-biased tail thrown outward, so the silhouette dissolves into
 *    individual cubes at the fringe instead of ending at a radius. Every burst
 *    here samples edge length on a cubic power law spanning roughly 6× and ties
 *    reach *inversely* to it: the big cubes stay in the fireball, the small
 *    ones are the shrapnel.
 *
 * 2. *Colour is a ramp in two variables, not a constant.* Every particle
 *    carries a ramp id and an offset into it. The offset is baked at spawn from
 *    how far out and how small the particle is, and the ramp then advances with
 *    age — so a single burst reads white-hot at the middle and orange-to-grey
 *    at the edge on the frame it appears, and the whole cloud walks down to
 *    smoke as it lives. Weapon classes name different ramps, so a prism hit and
 *    a shell hit are different events rather than the same white puff.
 *
 * 3. *Nothing here is additive, including the beams, and that is the fix rather
 *    than a regression.* Eight overlapping additive orange quads sum straight
 *    past white, which is why every impact in the game used to be the same flat
 *    white blob no matter what fired it — the colour was being computed
 *    correctly and then destroyed by the blend. Opaque cubes lit by nothing
 *    keep exactly the palette entry they were given (ARCHITECTURE §9: nothing
 *    may clip, every pixel points at a palette colour), and opaque blocks are
 *    what the reference is made of anyway. Beams were the last additive holdout
 *    on the argument that they are thin, few and genuinely light; the
 *    colour-discipline metric disagreed in the thousands of pixels, so a beam is
 *    now an opaque tapered bar with a chain of hot cubes riding down it. Light is
 *    expressed as *brightness within the palette*, never as a blend.
 *
 * 4. *A beat that lasts four ticks does not exist.* Every layer here is also
 *    sampled by a still frame — the capture set, and the eye during a fight —
 *    and a muzzle flash living 0.14 s is caught on one barrel in eight. Every
 *    short-lived layer therefore leaves something slower behind it: propellant
 *    smoke at the barrel outlives the flash five times over, a hulk smoulders for
 *    seconds after its fireball is out, and debris lies on the deck for ten. The
 *    fast layers are the event; the slow ones are the evidence it happened.
 *
 * A fourth, non-obvious one: **the capture harness renders at `dt = 0`.**
 * `src/dev/harness.ts` seeks the simulation and then draws two frames with no
 * elapsed time, so a particle is photographed in exactly the state it spawned
 * in. Scatter that exists only as velocity does not exist in a screenshot. Every
 * emitter below therefore places its particles at their spread-out positions
 * *and* gives them the velocity that would have carried them there, and the
 * colour ramp is offset per particle at spawn rather than only advanced by age.
 */

import {
  BoxGeometry,
  BufferAttribute,
  Color,
  Euler,
  InstancedMesh,
  Matrix4,
  MeshBasicMaterial,
  MeshLambertMaterial,
  Quaternion,
  Scene,
  Vector3,
  type Material,
} from 'three'
import type { Palette } from '../forge/palette.js'
import type { Fog } from './fog.js'
import { TICK_HZ } from '../core/config.js'
import * as fix from '../core/fixed.js'

/**
 * One simulation tick, in seconds. Effect time is measured in these and not in
 * wall-clock frames — see `fixedStep`.
 */
const TICK_DT = 1 / TICK_HZ

/** A beam in flight: two endpoints, a colour, and how long it has left. */
interface BeamPool {
  x0: Float32Array
  y0: Float32Array
  z0: Float32Array
  x1: Float32Array
  y1: Float32Array
  z1: Float32Array
  life: Float32Array
  maxLife: Float32Array
  width: Float32Array
  r: Float32Array
  g: Float32Array
  b: Float32Array
}

/** A particle. Flat arrays rather than objects: this pool is walked every frame. */
interface Pool {
  x: Float32Array
  y: Float32Array
  z: Float32Array
  vx: Float32Array
  vy: Float32Array
  vz: Float32Array
  /** Vertical acceleration. Negative for anything that falls, positive for smoke. */
  ay: Float32Array
  /** Velocity decay per second. Air resistance is what stalls a fireball. */
  drag: Float32Array
  /**
   * World Y this particle comes to rest at, or `NO_REST` for anything that
   * passes through the ground.
   *
   * Debris without it sinks through the terrain and is gone in half a second,
   * which loses the single most characteristic thing about the reference frames:
   * the ground stays littered with cubes long after the fire is out.
   */
  rest: Float32Array
  life: Float32Array
  maxLife: Float32Array
  size: Float32Array
  grow: Float32Array
  /**
   * Fraction of the lifetime over which the cube collapses to nothing.
   *
   * The dissolve is a *shrink*, not an alpha fade, and deliberately so. One
   * shared material means one opacity for every particle, and a per-instance
   * alpha attribute would blend effect colours against terrain colours and
   * invent hues that appear nowhere in the palette. A cube that shrinks out of
   * existence stays exactly on-palette for every pixel it ever covers, and it
   * is what the reference does: the cloud thins into fewer, smaller blocks.
   */
  tail: Float32Array
  /** Y squash. 1 for a cube, a sliver for a ground mark. */
  flat: Float32Array
  /** Euler orientation and angular velocity. Cubes tumble; billboards do not. */
  rx: Float32Array
  ry: Float32Array
  rz: Float32Array
  sx: Float32Array
  sy: Float32Array
  sz: Float32Array
  /** Fixed colour, used when `ramp` is zero. */
  r: Float32Array
  g: Float32Array
  b: Float32Array
  /**
   * Per-particle brightness, applied to whatever colour comes out of the ramp.
   *
   * A cloud whose cubes all land on the same ramp step is one flat value however
   * blocky it is, and a ramp is necessarily coarse — twelve steps for fire, six
   * for smoke — so neighbours collide constantly. A scalar in [0.78, 1] breaks
   * the mass into cubes again without inventing a colour: scaling every channel
   * by the same factor keeps the palette *direction* the colour-discipline gate
   * measures, which is exactly what "a palette colour under some amount of
   * light" means.
   */
  tint: Float32Array
  /** Index into `rampTable`. Zero means "use the fixed colour". */
  ramp: Uint8Array
  /** Where this particle starts on its ramp, before age advances it. */
  rampOff: Float32Array
  /** How fast age walks the ramp, as a multiple of normalised age. */
  rampRate: Float32Array
  /** 1 while the particle is alive. */
  live: Uint8Array
  /**
   * 1 for the lit pass, 0 for the unlit pass.
   *
   * The two are drawn by different meshes because they are different things.
   * Fire *is* light and belongs in an unlit pass, at full palette brightness
   * whatever the shadows are doing; a brick thrown off a dying tank and the
   * smoke above it are surfaces, and have to be lit or they read as glowing
   * sparks rather than as wreckage and soot.
   */
  solid: Uint8Array
  /**
   * Solid, but casts no shadow. Smoke.
   *
   * A plume is spawned at height, is buoyant, and lives for seconds. Given a hard
   * individual shadow on the ground under each cube, that reads as suspended
   * rubble rather than as smoke — four separate juries described the same frame as
   * "cubes hanging motionless in mid-air, each dropping a separate shadow". The
   * cubes are right; the shadows are what makes them read as debris.
   */
  shadowless: Uint8Array
}

const TAU = Math.PI * 2

/**
 * Fall rate for anything that is a lump of matter rather than a flame.
 *
 * Twice what it was, and the frame that pays for it is `air-strike-settled`: at
 * -6.5 a chunk thrown two cells up off a strike-scale blast was still in the air
 * 1.8 s later, so the shot meant to prove the explosion had *finished* caught
 * sixty cubes hanging over the crater at spawn size. Nothing here is simulating
 * real gravity — a cell is not a metre — so the only question this number
 * answers is how long ejecta may stay airborne, and the answer is "under a
 * second". The wreckage still lands and still lies there; it just stops
 * pretending to hover.
 */
const GRAVITY = -13

/** "This particle ignores the ground." `rest` is otherwise a height *above* terrain. */
const NO_REST = -1e9

/**
 * Where a muzzle sits, in world cells from the firing unit's centre.
 *
 * These are world-space offsets, not unscaled recipe coordinates.
 */
const MUZZLE_REACH = 1.12
const MUZZLE_HEIGHT = 0.7

/** The axis a unit box is stretched along to become a beam. */
const BEAM_FORWARD = new Vector3(0, 0, 1)

/** Blocks thrown by a dying unit. A structure throws proportionally more. */
const DEBRIS_PIECES = 16

/**
 * Beams on screen at once. A prism volley is one shot plus up to three feeder
 * links, so this is a lot of prisms firing at once.
 */
const MAX_BEAMS = 96

/**
 * How long a beam hangs in the air.
 *
 * The first version of this drew a line of tiny particles that lived two or
 * three frames, which is a tracer — right for a shell, wrong for a laser. A
 * beam has to persist long enough to be seen *as a line* and, for the prism
 * links, long enough to read as a network between several tanks rather than a
 * flicker.
 *
 * Half a second, up from a third. An instant weapon fires and lands on the same
 * tick, so a beam shorter-lived than its own impact produces the one thing a
 * volley must never look like: a hit with nothing that caused it. The
 * `prism-volley` capture was exactly that — a white burst on a walker and clear
 * air back to the tank that shot it.
 */
const BEAM_LIFE = 0.5
const LINK_LIFE = 0.7

/**
 * How much wider the sheath is than the beam's nominal width.
 *
 * The sheath is the *body* of the beam and the bright part is the chain of cubes
 * riding down the middle of it (`beamCore`), which is sized at one and a half to
 * three times the same width — so the core reliably breaks the sheath's outline
 * instead of hiding inside it. Widening one without the other closes that gap and
 * the beam goes back to being a bar of one colour.
 *
 * 1.0, down from 2.4, and the drop is a correction rather than a retune: this
 * number was chosen while `taperedPrism` was silently narrowing the whole
 * geometry to a fifth, so 2.4 was really 0.48 and every beam drew a hairline.
 * With the spindle fixed, 2.4 made a prism volley four overlapping slabs that
 * merged into one flat cyan wedge with nothing inside it — the same
 * "bar of one colour" failure from the other side. At 1.0 the sleeve is a
 * legible bar and the core cubes are still one and a half to three times it.
 */
const BEAM_SHEATH = 1.0

/**
 * The colour ramps, darkest-last, as linear RGB before palette snapping.
 *
 * Read them as a timeline of one cube: the instant of combustion, the flame,
 * the cooling ember, and the soot it leaves. A particle's ramp offset decides
 * where on this timeline it *starts*, which is what makes one burst show all
 * four stages at once instead of the whole cloud being one colour.
 */
const RAMP_SPECS: Record<string, number[][]> = {
  // Chemical fire: cordite, fuel, a shell going off.
  //
  // **These are written as palette entries, not as "what fire looks like".**
  // Every step is snapped to the nearest generated colour anyway, and a ramp
  // authored freehand snaps wherever it lands — the first pass at this asked for
  // a saturated mid-orange and got the *sand* entry, so a battlefield of
  // explosions came out the colour of the ground it was standing on. Naming the
  // entries directly makes the snap a no-op and the ramp mean what it says.
  // **The hot half of this ramp is deliberately over-sampled.** A ramp with one
  // white step and four reds spends four ninths of every particle's life in
  // ember red, and a battlefield of ember red reads as red plastic confetti —
  // which is exactly what the capture set showed. The reference's fire is
  // dominated by white, pale yellow and gold with orange only at the lobes and
  // red barely present at all, so the steps that carry that read are repeated.
  // Doubling an entry is not a hack: this table is walked by a normalised
  // offset, so repetition *is* how a ramp gets a weighting.
  //
  // **White is one step, not two.** Doubling it put a twelfth of the table on
  // pure white and the core cubes start at offset zero, so a detonation spent
  // roughly a third of its life as a flat blown-out white blob — larger on screen
  // than the units fighting under it, and with no shading anywhere in it because
  // the fire pass is unlit by design. One step is about three frames at 30 Hz,
  // which is the flash the reference has; the pale yellow that follows carries
  // the "hot" read the doubling was reaching for without the blowout.
  fire: [
    [1.0, 1.0, 1.0], // white-hot
    [0.937, 0.753, 0.302], // pale yellow
    [0.937, 0.753, 0.302],
    [0.937, 0.753, 0.302],
    [0.918, 0.675, 0.082], // gold
    [0.918, 0.675, 0.082],
    [0.965, 0.31, 0.035], // hot orange
    [0.965, 0.31, 0.035],
    [0.91, 0.306, 0.208], // orange-red
    [0.353, 0.22, 0.188], // dark ember
    [0.431, 0.42, 0.408], // grey smoke
    [0.204, 0.2, 0.196], // soot
  ],
  // Smoke that outlives the fire.
  //
  // These are *surface* colours: the smoke pass is lit, so a face turned away
  // from the sun renders at a fraction of what is written here. An earlier
  // version ran the ramp down to near-black and the shaded faces came out
  // effectively black — a wall of dark boxes over the battlefield rather than a
  // plume. The floor is set where the darkest lit face is still legibly grey,
  // and the top is near-white because the reference's smoke is white blocks.
  smoke: [
    [0.863, 0.859, 0.855],
    [0.671, 0.659, 0.651],
    [0.431, 0.42, 0.408],
    [0.365, 0.275, 0.255],
  ],
  // Thrown wreckage. Its own ramp, because it was borrowing `smoke`'s and that
  // ramp starts at near-white on purpose: smoke is a plume that wants to be pale,
  // and a lump of hull is not. A blast's ejecta came out as a wide field of flat
  // white cubes that never darkened and never tinted — paper confetti scattered
  // over more of the frame than the units fighting in it.
  //
  // So this starts where a lit concrete grey starts, walks to grey-brown, and
  // ends on the scorch colour: the piece cools, collects dust, and finishes the
  // colour of the ground it is lying on. Nothing here is emissive — the ejecta
  // spawn `solid`, so they take the same directional light as the terrain and
  // every cube shows three differently shaded faces.
  //
  // **The bottom step is gone, and the pass being lit is why.** The ramp ended
  // on 0.28/0.21/0.20, which a shaded face halves again and a value jitter
  // takes down another fifth: a settled chunk rendered at about 8% grey, which
  // is not "the colour of the ground it is lying on", it is a black square.
  // Scattered over terrain those read as props rather than as wreckage — the
  // battlefield looked littered with rocks nobody placed. Ending on the
  // grey-brown step keeps the cool-and-collect-dust walk and keeps the piece a
  // visible object at the end of it.
  debris: [
    [0.671, 0.659, 0.651],
    [0.541, 0.51, 0.494],
    [0.431, 0.42, 0.408],
    [0.365, 0.275, 0.255],
  ],
  // Ground kicked up by a blast. Warmer than smoke, because it is regolith —
  // but it has to start *lighter* than the ground it came off, or a dust plume
  // over Martian soil is the same colour as Martian soil and reads as nothing.
  dust: [
    [0.871, 0.741, 0.529],
    [0.831, 0.694, 0.592],
    [0.776, 0.6, 0.463],
    [0.725, 0.506, 0.337],
    [0.541, 0.408, 0.376],
  ],
  // The detonation itself, and nothing else in this file starts here.
  //
  // `fire` spends one twelfth of its table on white and then walks, which is
  // right for a fireball that has to cool — but it means a burst photographed
  // anywhere past its first beat has no white in it at all, and an impact with
  // no blown-out core reads as a bag of gravel however hot the gold around it
  // is. This ramp holds white for half its length and only falls to gold at the
  // very end, so the flash layer is white for as long as it exists and hands
  // off to the flame rather than winking out.
  flash: [
    [1, 1, 1],
    [1, 1, 1],
    [1, 1, 1],
    [0.937, 0.753, 0.302],
    [0.918, 0.675, 0.082],
  ],
  // The mark left behind. A scorch does not cool, it just is.
  scorch: [
    [0.263, 0.161, 0.137],
    [0.282, 0.212, 0.196],
    [0.353, 0.22, 0.188],
  ],
}

/** How many ramp steps a particle walks over its whole life, by default. */
const RAMP_RATE = 1

export class Effects {
  /**
   * Terrain height at a world position, injected by the renderer the same way
   * `bars` and `entityRenderer` get it.
   *
   * Debris needs this because it *travels*. `rest` used to be an absolute Y worked
   * out from the ground under the explosion, so a cube thrown over a cliff step
   * stopped at the height of the ground it left rather than the ground it reached,
   * and hung there with its shadow on the terrain far below. Four separate juries
   * described that frame as "cubes hanging motionless in mid-air".
   */
  groundAt: ((x: number, z: number) => number) | null = null
  /** Hidden particles still age, but cannot render or cast shadows. */
  visibleAt: ((x: number, z: number) => boolean) | null = null

  private readonly mesh: InstancedMesh
  private readonly solidMesh: InstancedMesh
  private readonly plumeMesh: InstancedMesh
  private readonly beamMesh: InstancedMesh
  private readonly beams: BeamPool
  private beamCount = 0
  private readonly beamFrom = new Vector3()
  private readonly beamTo = new Vector3()
  private readonly beamAxis = new Vector3()
  private readonly beamQuat = new Quaternion()
  /** Scratch for `beamRgb`, so a shot does not allocate a tuple. */
  private readonly beamTint = new Float32Array(3)
  private readonly pool: Pool
  private readonly capacity: number
  /**
   * Per-burst particle budget as a fraction of the tuned figures below.
   *
   * Derived from the quality preset rather than read from it directly: the
   * preset's number was chosen when a burst was a handful of large billboards,
   * and a cube burst is made of many small cubes, so the same visual costs more
   * slots. The preset still scales the pool and the counts — it just scales
   * them against a different unit.
   */
  private readonly density: number
  private next = 0

  private readonly scratchMatrix = new Matrix4()
  private readonly scratchPos = new Vector3()
  private readonly scratchScale = new Vector3()
  private readonly scratchColor = new Color()
  private readonly scratchEuler = new Euler()
  private readonly scratchQuat = new Quaternion()

  /** Ramp id 0 is "no ramp"; the table is 1-based to keep that check branchless. */
  private readonly rampTable: Float32Array[] = [new Float32Array(3)]
  private readonly rampIds = new Map<string, number>()

  constructor(
    scene: Scene,
    private readonly palette: Palette,
    budget = 512,
  ) {
    // Six slots per budgeted particle, not four. The pool is a ring: a spawn
    // overwrites whatever slot is next whether or not it is still alive, so the
    // *layer count* sets how long the longest-lived layers survive. Once every
    // shot laid down a muzzle cone, a tracer chain and an impact, a ten-second
    // scorch was being recycled inside two — the battlefield lost its history to
    // the very beats that were added to give it one.
    this.capacity = Math.min(6144, Math.max(512, budget * 6))
    this.density = Math.min(1.6, Math.max(0.25, budget / 512))
    const capacity = this.capacity

    // Unlit and **opaque**. Fire is light: lighting it would make it dimmer in
    // shadow, which is exactly backwards. It used to be additive as well, and
    // that is what turned every explosion in the game into the same flat white
    // cotton ball — additive orange summed over eight overlapping quads clips
    // past white, so the ramp below would have been computed and then thrown
    // away. Opaque instances keep the palette entry they were handed.
    //
    // No `vertexColors`, for the same reason the health bars do without it: a
    // box has no colour attribute, and asking the material for one it does not
    // have renders every instance black.
    //
    // Per-particle colour comes from `instanceColor`, which is a separate path.
    //
    // `vertexColors` *is* on, and it is not lighting. The attribute holds one
    // scalar per face, baked once: a cube drawn unlit at one flat value is a
    // hexagon with no interior in it, and a dozen of them at the same ramp step
    // weld into a single flat polygon with a hard vector edge — which is what a
    // fireball here photographed as. The reference's flame is made of cubes you
    // can count because each one shows three different values. A per-face scalar
    // multiply keeps the palette direction the discipline gate measures, so the
    // cubes gain faces without gaining colours.
    const material = new MeshBasicMaterial({ vertexColors: true })
    this.mesh = new InstancedMesh(shadedBox(), material, capacity)
    this.mesh.frustumCulled = false
    this.mesh.count = 0
    scene.add(this.mesh)

    // Debris: lit, opaque, and casting shadows like everything else made of
    // blocks. Sharing the unlit material made wreckage glow.
    const solidMaterial = new MeshLambertMaterial({ flatShading: true })
    this.solidMesh = new InstancedMesh(new BoxGeometry(1, 1, 1), solidMaterial, capacity)
    this.solidMesh.frustumCulled = false
    this.solidMesh.castShadow = true
    this.solidMesh.count = 0
    scene.add(this.solidMesh)

    // Smoke: the same lit, opaque cubes, and deliberately no shadow.
    //
    // `castShadow` is per mesh in three, not per instance, so telling smoke apart
    // from wreckage costs a second instanced mesh. It is worth one draw call: a
    // plume hangs at height for seconds by design, and a hard individual shadow
    // under every cube of it is what turned smoke into what four juries read as
    // "cubes hanging motionless in mid-air". The cubes are the intended style. The
    // shadows are what made them look like suspended rubble.
    this.plumeMesh = new InstancedMesh(
      new BoxGeometry(1, 1, 1),
      new MeshLambertMaterial({ flatShading: true }),
      capacity,
    )
    this.plumeMesh.frustumCulled = false
    this.plumeMesh.castShadow = false
    this.plumeMesh.receiveShadow = true
    this.plumeMesh.count = 0
    scene.add(this.plumeMesh)

    // Beams are their own mesh rather than particles. A laser is a continuous
    // line, and a line made of particles is a dotted line however many of them
    // there are — the geometry is one spindle stretched between two points,
    // which is both cheaper and the thing it is meant to look like.
    //
    // **This pass used to be additive and no longer is, and the gate found it.**
    // An additive layer sums with whatever is behind it, so every pixel of every
    // beam was a colour that appears in no palette (§9) — measurably: the shots
    // with beams in them carried thousands of off-palette pixels, and the
    // brightest offender was the *fallback* colour a non-energy beam got when its
    // accent lookup missed, an arbitrary warm orange picked in code. Opaque and
    // snapped, a beam is a bright bar at full palette brightness, which is both
    // what the reference's beams look like and legal. The fade scales the colour
    // rather than blending it, and a scaled palette entry is still that entry
    // under less light.
    const beamMaterial = new MeshBasicMaterial({ vertexColors: true })
    this.beamMesh = new InstancedMesh(taperedPrism(), beamMaterial, MAX_BEAMS)
    this.beamMesh.frustumCulled = false
    this.beamMesh.count = 0
    scene.add(this.beamMesh)

    const bf = () => new Float32Array(MAX_BEAMS)
    this.beams = {
      x0: bf(), y0: bf(), z0: bf(),
      x1: bf(), y1: bf(), z1: bf(),
      life: bf(), maxLife: bf(), width: bf(),
      r: bf(), g: bf(), b: bf(),
    }

    const f = () => new Float32Array(capacity)
    this.pool = {
      x: f(), y: f(), z: f(),
      vx: f(), vy: f(), vz: f(),
      ay: f(), drag: f(), rest: new Float32Array(capacity).fill(NO_REST),
      life: f(), maxLife: f(),
      size: f(), grow: f(), tail: f(), flat: f(),
      rx: f(), ry: f(), rz: f(),
      sx: f(), sy: f(), sz: f(),
      r: f(), g: f(), b: f(),
      tint: new Float32Array(capacity).fill(1),
      ramp: new Uint8Array(capacity),
      rampOff: f(),
      rampRate: f(),
      live: new Uint8Array(capacity),
      solid: new Uint8Array(capacity),
      shadowless: new Uint8Array(capacity),
    }
  }

  // --- ramps ---------------------------------------------------------------

  /**
   * Resolve a ramp name to a table id, building it on first use.
   *
   * Every step is snapped to a real palette entry. That is not decoration: the
   * colour-discipline gate requires every pixel on screen to point at a
   * generated palette colour, and these instances are unlit, so whatever is
   * written here reaches the framebuffer unchanged. Snapping once at build time
   * costs one lookup per ramp for the life of the process.
   */
  private rampId(name: string): number {
    const existing = this.rampIds.get(name)
    if (existing !== undefined) return existing

    let steps: number[][] | null = name === 'smoke' ? this.neutralSteps() : (RAMP_SPECS[name] ?? null)
    if (!steps && name.startsWith('energy:')) steps = this.energySteps(name.slice(7))
    if (!steps) steps = RAMP_SPECS.fire

    const table = new Float32Array(steps.length * 3)
    for (let i = 0; i < steps.length; i++) {
      const [r, g, b] = this.snap(steps[i][0], steps[i][1], steps[i][2])
      table[i * 3] = r
      table[i * 3 + 1] = g
      table[i * 3 + 2] = b
    }
    // Ids are stored in a Uint8Array, so the table cannot outgrow 255 entries.
    // There is one ramp per weapon class plus one per owner accent, which is a
    // dozen; the clamp is a backstop against a caller inventing names per shot.
    if (this.rampTable.length >= 255) return 1
    this.rampTable.push(table)
    const id = this.rampTable.length - 1
    this.rampIds.set(name, id)
    return id
  }

  /**
   * A beam weapon's impact ramp, built from the firing side's accent.
   *
   * White at the point of contact, the accent hue through the middle, and a
   * cold dark violet-grey as it dies — so a prism hit is a *different event*
   * from a shell hit rather than the same orange puff. This is the single
   * clearest way to tell weapon classes apart in a still frame.
   */
  private energySteps(accent: string): number[][] {
    const [r, g, b] = this.accent(accent, 1)
    const [dr, dg, db] = this.accent(accent, 0)
    return [
      [1, 1, 1],
      // A third white, not half. At half, an energy impact's largest cubes came
      // out pastel — a violet weapon threw pale pink slabs the size of a hull,
      // which reads as paper rather than as a discharge. Keeping the accent
      // dominant one step in gives a white core with saturated colour around it.
      [0.34 + r * 0.66, 0.34 + g * 0.66, 0.34 + b * 0.66],
      [r, g, b],
      [dr, dg, db],
      [dr * 0.5 + 0.08, dg * 0.45 + 0.07, db * 0.55 + 0.1],
      [0.24, 0.22, 0.26],
    ]
  }

  /**
   * The smoke ramp, taken from the palette's own neutral ramp.
   *
   * Written out as hand-picked greys it went *green*. The palette tops itself up
   * with warm and greenish desaturated fillers so the quantizer has somewhere to
   * land, and an arbitrary grey is usually nearer one of those than to a real
   * neutral entry — so every smoke cube snapped to olive and a burning base
   * smoked khaki. The neutral ramp is the six greys the palette guarantees; the
   * pale end is repeated so the plume spends most of its life light, the way the
   * reference's smoke columns do, and only sinks to mid-grey as it disperses.
   */
  private neutralSteps(): number[][] {
    const n = this.palette.neutralRamp.indices
    const at = (i: number): number[] => {
      const [r, g, b] = this.palette.rgb(n[Math.min(i, n.length - 1)])
      return [r / 255, g / 255, b / 255]
    }
    // Starts at the ramp's white anchor rather than one step below it. The pass
    // is lit, so a face turned away from the sun already renders this at a
    // fraction of what is written — an albedo of 0.86 shades to mid grey, and a
    // plume of mid grey is soot rather than the wall of white blocks the
    // reference throws off a detonation.
    //
    // **The white anchor is one step now, not two.** Doubling it put a third of
    // every plume's walk on pure white, and a plume is the longest-lived thing
    // in the pool — so that is what a *late* frame catches: 1.7 s after a strike
    // the survivors were still near-white blocks at better than half size, which
    // four juries in a row read as suspended rubble rather than as smoke going
    // out. Fresh smoke is still the white anchor, which is the beat the
    // reference's detonation column shows; it simply stops being the first third
    // of the plume's whole life.
    //
    // One step down, not two, and the difference is not cosmetic. Collapsing
    // the pale middle as well took the *body* of a young plume to mid grey, and
    // a burst frame came back with a wall of dark boxes standing in it — the
    // exact failure the paragraph above this one is about, arrived at from the
    // other side. The pale steps carry the plume; only the anchor was overweight.
    return [at(5), at(4), at(4), at(3), at(3), at(2)]
  }

  /** The nearest real palette entry to an RGB triple, back as linear RGB. */
  private snap(r: number, g: number, b: number): [number, number, number] {
    const q = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)))
    const [pr, pg, pb] = this.palette.rgb(this.palette.nearest(q(r), q(g), q(b)))
    return [pr / 255, pg / 255, pb / 255]
  }

  // --- emitters ------------------------------------------------------------

  /**
   * A shot leaving a barrel: a bright block at the muzzle, sparks thrown wide
   * of it, and a wisp of propellant smoke drifting off the side.
   *
   * The offsets matter more than they look. This used to spawn 0.35 cells
   * forward and 0.3 up, which are the numbers from the *recipe* — but units are
   * drawn at one and a half times their model scale, so every flash appeared
   * inside the hull that fired it. The game had muzzle flashes the whole time
   * and none of them were ever visible.
   *
   * Deliberately cheap: with three dozen units trading fire this is the highest
   * frequency emitter in the game, and every slot it takes is a slot an
   * explosion does not get.
   */
  muzzleFlash(
    x: fix.Fixed,
    y: fix.Fixed,
    tx: fix.Fixed,
    ty: fix.Fixed,
    ground: number,
    ramp = 'fire',
  ): void {
    const sx = fix.toFloat(x)
    const sz = fix.toFloat(y)
    const dx = fix.toFloat(tx) - sx
    const dz = fix.toFloat(ty) - sz
    const len = Math.hypot(dx, dz) || 1
    const ux = dx / len
    const uz = dz / len
    const seed = seedOf(sx, sz)
    const rampId = this.rampId(ramp)
    const mx = sx + ux * MUZZLE_REACH
    const mz = sz + uz * MUZZLE_REACH

    // **An energy weapon has no propellant.** Everything below was written for a
    // gun: a cone of burning gas, sparks off the crown, and a puff of smoke that
    // hangs for a second. Given to a prism tank firing four times inside half a
    // second, the smoke alone — three cubes each growing from 0.2 to nearly half
    // a cell — parked a clump of pale blocks on top of the turret that the volley
    // capture read as damage debris sitting on the muzzle. What leaves an
    // aperture is light: a smaller flash, in the weapon's own colour rather than
    // white, and nothing left behind it.
    const energy = ramp.startsWith('energy:')
    const muzzle = energy ? 0.62 : 1

    // The flash itself: a cone of three cubes stepping out along the barrel,
    // largest and whitest at the muzzle. One cube was a dot — at 24 px per cell
    // a 0.21 cell cube is five pixels, and five pixels for a tank's main gun is
    // why a firing line of thirty units photographed as thirty idle models. A
    // cone also gives the flash a *direction*, which is what says a shot left
    // this barrel rather than something popped next to it.
    for (let k = 0; k < 3; k++) {
      const step = k * 0.19
      this.spawn({
        x: mx + ux * step,
        y: ground + MUZZLE_HEIGHT + k * 0.02,
        z: mz + uz * step,
        vx: ux * (1.1 + k * 0.5),
        vy: 0.25,
        vz: uz * (1.1 + k * 0.5),
        // Long enough to be caught. A 0.14 s flash is four ticks, so a still
        // frame of a battle sees roughly one barrel in eight lit; at 0.2 s it
        // sees several, which is what the reference frame shows.
        life: 0.2 - k * 0.03,
        size: (0.38 - k * 0.09) * muzzle,
        grow: -0.9,
        tail: 0.7,
        ramp: rampId,
        // An energy muzzle starts one step in, on the accent rather than on the
        // white anchor. A white cube at a beam weapon's aperture is
        // indistinguishable from a chip of hull; a cyan one is the weapon firing.
        rampOff: (energy ? 0.19 : 0.02) + k * 0.06,
        // **A flash does not cool, it stops.** At the default rate a 0.2 s flash
        // walks the entire ramp, so by the time a frame catches one it is a small
        // orange-red cube — which is what every barrel in the capture set was
        // wearing, and why the shot read as if nobody was firing. Held near the
        // white end and taken away by the shrink instead.
        rampRate: 0.3,
        spin: 3,
        seed,
        index: k,
      })
    }
    // Sparks thrown wide of it, offset in *position* as well as velocity — the
    // capture harness draws at dt = 0, so anything whose spread lives only in
    // its velocity is photographed as a single dot.
    for (let k = 0; k < 4; k++) {
      const side = k % 2 === 0 ? -1 : 1
      const out = 0.16 + (k >> 1) * 0.22
      this.spawn({
        x: mx + (ux * out - uz * 0.13 * side),
        y: ground + MUZZLE_HEIGHT + 0.07,
        z: mz + (uz * out + ux * 0.13 * side),
        vx: ux * 1.7 - uz * 0.7 * side,
        vy: 0.55,
        vz: uz * 1.7 + ux * 0.7 * side,
        life: 0.15,
        size: (0.13 + rand01(seed, k, 14) * 0.08) * muzzle,
        grow: -0.5,
        tail: 0.8,
        ramp: rampId,
        rampOff: (energy ? 0.2 : 0.1) + k * 0.06,
        rampRate: 0.4,
        spin: 6,
        seed,
        index: 3 + k,
      })
    }
    // Propellant, and the layer that does the most work per slot in the whole
    // file. The flash is four to six ticks and a still frame mostly misses it;
    // the smoke hangs for a second, so a firing line photographed at any instant
    // has a white puff at every barrel that has fired recently — which is
    // precisely what the reference's battle frame shows, and the read our capture
    // set was missing even when the shooting was happening.
    if (energy) return
    const smokeRamp = this.rampId('smoke')
    for (let k = 0; k < 3; k++) {
      const back = 0.1 + k * 0.16
      this.spawn({
        x: mx - ux * back + uz * (rand01(seed, k, 8) - 0.5) * 0.16,
        y: ground + MUZZLE_HEIGHT + 0.12 + k * 0.06,
        z: mz - uz * back - ux * (rand01(seed, k, 8) - 0.5) * 0.16,
        vx: ux * 0.35,
        vy: 0.5,
        vz: uz * 0.35,
        ay: 0.35,
        drag: 1.1,
        life: 0.8 + rand01(seed, k, 9) * 0.5,
        size: 0.2 - k * 0.03,
        grow: 0.22,
        tail: 0.55,
        ramp: smokeRamp,
        rampOff: 0.06 + k * 0.12,
        rampRate: 0.5,
        solid: true,
        shadowless: true,
        spin: 1.4,
        seed,
        index: 7 + k,
      })
    }
  }

  /**
   * A shot with no travel time: a line from the barrel to whatever it hit.
   *
   * `colour` names an accent ramp, so a prism beam and a rifle tracer are
   * visibly different weapons rather than the same orange streak at different
   * lengths.
   */
  beam(
    x: fix.Fixed,
    y: fix.Fixed,
    tx: fix.Fixed,
    ty: fix.Fixed,
    ground: number,
    colour = 'fire',
    width = 0.09,
    life = BEAM_LIFE,
  ): void {
    const x0 = fix.toFloat(x)
    const z0 = fix.toFloat(y)
    const y0 = ground + MUZZLE_HEIGHT
    const x1 = fix.toFloat(tx)
    const z1 = fix.toFloat(ty)
    const y1 = ground + MUZZLE_HEIGHT * 0.7
    this.addBeam(x0, y0, z0, x1, y1, z1, colour, width, life)
    this.beamCore(x0, y0, z0, x1, y1, z1, colour, width, life)
    this.beamImpact(x1, y1, z1, colour, width)
  }

  /**
   * The far end of an instant shot: the beat that says it arrived.
   *
   * `weapon:impact` already fires a burst, but it is placed on the *ground* under
   * the target and sized by the weapon's splash, which for a beam is zero — so a
   * prism volley photographed as four lit lines converging on a walker that was
   * visibly untouched, with a couple of pale chips near its feet as the only
   * evidence. Contact happens at the beam's own height, not on the deck, and it
   * has to be several times the beam's width or it hides inside the line that
   * caused it.
   *
   * Everything here is shorter-lived than the beam on purpose. The beam is the
   * afterimage; this is the instant, and an impact flash that outlives the shot
   * is the thing that reads as debris parked on a hull.
   */
  private beamImpact(x: number, y: number, z: number, colour: string, width: number): void {
    const seed = seedOf(x, z) ^ 0x1d2c9f37
    const rampId = this.rampId(colour === 'fire' ? 'fire' : `energy:${colour}`)
    // The flash: one hot block at the point of contact, collapsing out of
    // existence inside a fifth of a second.
    this.spawn({
      x,
      y,
      z,
      life: 0.16,
      size: width * 5,
      grow: -width * 4,
      tail: 0.9,
      ramp: rampId,
      rampOff: 0,
      rampRate: 0.9,
      spin: 3,
      seed,
      index: 80,
    })
    // The spray: small cubes thrown back off the hull in the weapon's own
    // colour, so the hit is legibly *this* weapon's rather than a white puff.
    const n = clampInt(Math.round(7 * this.density), 4, 12)
    for (let i = 0; i < n; i++) {
      const a = rand01(seed, i, 0) * TAU
      const u = rand01(seed, i, 1)
      const size = width * (0.5 + 1.9 * u * u * u)
      const out = width * (1.2 + rand01(seed, i, 2) * 4.5)
      const cosPhi = -0.1 + rand01(seed, i, 3) * 1.1
      const sinPhi = Math.sqrt(Math.max(0, 1 - cosPhi * cosPhi))
      this.spawn({
        x: x + Math.cos(a) * sinPhi * out,
        y: y + cosPhi * out,
        z: z + Math.sin(a) * sinPhi * out,
        vx: Math.cos(a) * sinPhi * 3.2,
        vy: cosPhi * 3.2 + 0.6,
        vz: Math.sin(a) * sinPhi * 3.2,
        ay: GRAVITY * 0.5,
        drag: 4,
        life: 0.18 + rand01(seed, i, 4) * 0.16,
        size,
        grow: -size * 0.5,
        tail: 0.7,
        ramp: rampId,
        // Straddles white and the accent: the core of the hit is blown out, the
        // spray around it carries the colour.
        rampOff: 0.04 + rand01(seed, i, 5) * 0.3,
        rampRate: 1.1,
        tint: 0.78 + rand01(seed, i, 6) * 0.22,
        spin: 9,
        seed,
        index: 81 + i,
      })
    }
  }

  /**
   * The bright middle of a shot: a chain of opaque cubes laid down the line.
   *
   * Two problems, one answer. The additive sheath cannot be made bright without
   * clipping — it sums with lit ground, and a clipped channel is a colour the
   * palette never contained (§9) — and a beam of constant width reads as a line
   * drawn on the frame rather than as a round travelling down it. A chain of
   * discrete cubes of varying size solves both: every one of them is an exact
   * palette entry however many overlap, and the gaps and the size jitter are the
   * reference's tracer exactly, which is a dotted string of white blocks and not
   * a stroke.
   *
   * Placed along the whole path rather than at the muzzle, because with no travel
   * time in the simulation this *is* the projectile — the only frame it gets is
   * the one where it is already everywhere between the barrel and the target.
   */
  private beamCore(
    x0: number,
    y0: number,
    z0: number,
    x1: number,
    y1: number,
    z1: number,
    colour: string,
    width: number,
    life: number,
  ): void {
    const dx = x1 - x0
    const dy = y1 - y0
    const dz = z1 - z0
    const len = Math.hypot(dx, dz)
    if (len < 0.2) return
    const seed = seedOf(x0, z0) ^ 0x4f1bbcdd
    const rampId = this.rampId(colour === 'fire' ? 'fire' : `energy:${colour}`)
    // One cube per half cell, capped: a shot across eight cells is a chain, not
    // a solid rope, and the cap is what keeps a firing line from owning the pool.
    const n = clampInt(Math.round(len / 0.55), 2, 8)
    // Lateral jitter runs along the ground-plane perpendicular, so the chain
    // wanders around the axis instead of standing in a perfect row.
    const nx = -dz / len
    const nz = dx / len
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n
      const off = (rand01(seed, i, 0) - 0.5) * width * 1.3
      const size = width * (1.7 + rand01(seed, i, 1) * 2.0)
      this.spawn({
        x: x0 + dx * t + nx * off,
        y: y0 + dy * t + (rand01(seed, i, 2) - 0.5) * width,
        z: z0 + dz * t + nz * off,
        vx: (dx / len) * 1.4,
        vz: (dz / len) * 1.4,
        // Slightly shorter than the sheath: the halo is the afterimage and the
        // core is the event, so the cubes wink out first and leave the glow
        // behind them. **Slightly**, though — at 0.55 half the chain was gone
        // before the beam was, and any still frame that caught a volley past its
        // first third photographed a coloured bar with no hot middle at all,
        // which is the "flat line, no core" the volley shot kept showing. This
        // is not a longer beat: the beam already lasts `life`, and the core is
        // what the beam is made of.
        life: life * (0.8 + rand01(seed, i, 3) * 0.2),
        size,
        // Was -0.5 of its own edge per second, which took a third off the chain
        // inside the beam's own life and left cubes small enough to hide inside
        // the sheath. The core has to stay bigger than the bar it rides down or
        // there is no core.
        grow: -size * 0.22,
        tail: 0.75,
        ramp: rampId,
        rampOff: 0.02 + rand01(seed, i, 4) * 0.14,
        // Barely walks. A round in flight is white for as long as it exists; the
        // ramp is here to give the chain variety along its length, not to cool
        // it — and at 0.3 it cooled anyway, so the chain had reached the accent
        // colour by the time a still frame caught it and the beam was one hue
        // from end to end.
        rampRate: 0.16,
        spin: 2.5,
        seed,
        index: 60 + i,
      })
    }
  }

  /**
   * The link between two prism tanks feeding one shot.
   *
   * Thinner than the shot itself and longer-lived, so that three tanks
   * standing together read as a connected network for a moment rather than as
   * three unrelated flashes.
   */
  link(x: fix.Fixed, y: fix.Fixed, tx: fix.Fixed, ty: fix.Fixed, ground: number, colour: string): void {
    const h = ground + MUZZLE_HEIGHT * 1.15
    const x0 = fix.toFloat(x)
    const z0 = fix.toFloat(y)
    const x1 = fix.toFloat(tx)
    const z1 = fix.toFloat(ty)
    this.addBeam(x0, h, z0, x1, h, z1, colour, 0.06, LINK_LIFE)
    // A few charge cubes crawling the link. The sheath alone is a hairline at
    // this width, and a hairline between two tanks is indistinguishable from an
    // artefact; the cubes are what make the feeder read as energy moving.
    this.beamCore(x0, h, z0, x1, h, z1, colour, 0.06, LINK_LIFE)
  }

  private addBeam(
    x0: number,
    y0: number,
    z0: number,
    x1: number,
    y1: number,
    z1: number,
    colour: string,
    width: number,
    life: number,
  ): void {
    if (Math.hypot(x1 - x0, z1 - z0) < 0.01) return
    // Oldest-first reuse when full. A dropped beam is one frame of one shot;
    // growing the pool to guarantee otherwise would cost more than it is worth.
    let i = this.beamCount
    if (i >= MAX_BEAMS) {
      i = 0
      for (let k = 1; k < MAX_BEAMS; k++) {
        if (this.beams.life[k] < this.beams.life[i]) i = k
      }
    } else {
      this.beamCount++
    }
    this.beamRgb(colour)
    const p = this.beams
    p.x0[i] = x0
    p.y0[i] = y0
    p.z0[i] = z0
    p.x1[i] = x1
    p.y1[i] = y1
    p.z1[i] = z1
    p.life[i] = life
    p.maxLife[i] = life
    p.width[i] = width * BEAM_SHEATH
    p.r[i] = this.beamTint[0]
    p.g[i] = this.beamTint[1]
    p.b[i] = this.beamTint[2]
  }

  /**
   * Age every beam and write its instance.
   *
   * The box is unit-sized and stretched along its own Z, then rotated to point
   * from one end to the other — the same trick the selection rings use, and
   * the reason a beam is one instance rather than a string of them.
   */
  private ageBeams(dt: number): void {
    const p = this.beams
    for (let i = 0; i < this.beamCount; i++) {
      if (p.life[i] > 0) p.life[i] -= dt
    }
  }

  private writeBeams(sub: number): void {
    const p = this.beams
    let live = 0
    for (let i = 0; i < this.beamCount; i++) {
      const life = p.life[i] - sub
      if (life <= 0) continue

      this.beamFrom.set(p.x0[i], p.y0[i], p.z0[i])
      this.beamTo.set(p.x1[i], p.y1[i], p.z1[i])
      this.beamAxis.subVectors(this.beamTo, this.beamFrom)
      const length = this.beamAxis.length()
      if (length < 1e-4) continue
      this.beamAxis.divideScalar(length)
      this.beamQuat.setFromUnitVectors(BEAM_FORWARD, this.beamAxis)

      this.scratchPos.addVectors(this.beamFrom, this.beamTo).multiplyScalar(0.5)
      // Fades on a curve rather than linearly: a laser is at its brightest the
      // instant it fires and lingers as an afterimage.
      //
      // The floor is what makes it an afterimage rather than a disappearing act,
      // and it is high — 0.65 — because this scales an *opaque* palette entry
      // now. A factor of a half on a mid-lightness accent is a grey-blue line,
      // which is what the capture showed: the beams were there, drawn correctly,
      // and the colour had been dimmed out of them. Brightness, not opacity, is
      // the only dial a palette-disciplined renderer has.
      //
      // 0.8, not 0.65, for the same reason again: at 0.65 a cyan accent lands
      // two thirds of the way to grey, which is exactly the "flat grey-white
      // line with no colour in it" a volley photographed as. A higher floor is
      // also *better* discipline, not worse — this multiply is the one place a
      // beam colour leaves the palette, and 0.8 lands nearer the entry it came
      // from than 0.65 does.
      const t = life / p.maxLife[i]
      const fade = 0.8 + 0.2 * t * t
      this.scratchScale.set(p.width[i] * (0.5 + fade * 0.5), p.width[i] * (0.5 + fade * 0.5), length)
      this.scratchMatrix.compose(this.scratchPos, this.beamQuat, this.scratchScale)
      this.beamMesh.setMatrixAt(live, this.scratchMatrix)
      this.scratchColor.setRGB(p.r[i] * fade, p.g[i] * fade, p.b[i] * fade)
      this.beamMesh.setColorAt(live, this.scratchColor)
      live++
    }
    this.beamMesh.count = live
    this.beamMesh.instanceMatrix.needsUpdate = true
    if (this.beamMesh.instanceColor) this.beamMesh.instanceColor.needsUpdate = true

    // Compacted so the loop above stays bounded by what is actually alive.
    if (live === 0) this.beamCount = 0
  }

  /**
   * A shell in flight. Called every frame for every live projectile rather
   * than spawned once: the simulation owns where they are, and interpolating a
   * copy would let the tracer and the damage disagree.
   *
   * The head is a hot cube; the trail behind it is smoke, emitted on a *spatial*
   * stagger rather than a temporal one. Emitting per frame ties the trail's
   * density to the frame rate — a fast machine draws a solid rope and a slow one
   * draws three dots — whereas keying it to the quantised position means a bomb
   * lays down the same trail however fast the page is running, and it is what
   * makes a falling bomb visible at all instead of a 4-pixel speck.
   */
  tracer(x: number, z: number, ground: number, height = 0.45, scale = 1, ramp = 'fire'): void {
    const seed = seedOf(x, z)
    this.spawn({
      x,
      y: ground + height,
      z,
      life: 0.09,
      size: 0.17 * scale,
      grow: -0.4,
      tail: 0.6,
      ramp: this.rampId(ramp),
      rampOff: 0,
      spin: 4,
      seed,
      index: 0,
    })
    // One puff every few sixteenths of a cell along the flight path.
    const cell = ((x * 6) | 0) + ((z * 6) | 0) * 31
    if ((cell & 1) !== 0) return
    this.spawn({
      x,
      y: ground + height - 0.04,
      z,
      vy: 0.16,
      ay: 0.2,
      drag: 1.4,
      life: 0.55 + rand01(seed, 1, 9) * 0.35,
      size: 0.1 * scale,
      grow: 0.34 * scale,
      tail: 0.6,
      ramp: this.rampId('smoke'),
      rampOff: 0.3,
      solid: true,
      shadowless: true,
      spin: 1.2,
      seed,
      index: 1,
    })
  }

  /**
   * An impact, in five layers: fireball, shrapnel, ground ring, smoke, scorch.
   *
   * `scale` is the blast radius in the loosest sense — 0.8 for a rifle round,
   * 1.6 for a splash weapon, 2.2 for a unit coming apart, 3+ for ordnance. Every
   * layer keys off it, and the layers switch on progressively so a small-arms
   * hit stays a spark and a bomb leaves a mark on the map.
   */
  explosion(x: fix.Fixed, y: fix.Fixed, ground: number, scale = 1, ramp = 'fire'): void {
    const px = fix.toFloat(x)
    const pz = fix.toFloat(y)
    this.burst(px, pz, ground, scale, ramp)
    // The detonation beat. Two to four ticks, at the front of everything else.
    if (scale >= 1.0) this.flash(px, pz, ground, scale)
    // The flash on the ground, before anything else keys off scale. A detonation
    // beside a tank throws light on the dirt, and without it the fireball is a
    // prop standing on the terrain rather than an event happening to it. Lights
    // are out — §9 keeps both of them neutral, so a warm one is off-palette by
    // construction — so the spill is painted instead of lit.
    if (scale >= 1.0) this.glow(px, pz, ground, scale, ramp)
    if (scale >= 1.15) this.ring(px, pz, ground, scale)
    if (scale >= 0.9) this.smoke(px, pz, ground, scale, Math.round(15 * scale * this.density))
    // A dead unit leaves a mark too, not only ordnance. The threshold used to
    // sit above the 1.4 a unit death passes, so a fight could destroy a dozen
    // vehicles and hand the ground back spotless the moment the smoke cleared —
    // and a battlefield with no history in it is the thing the reference frames
    // have most and ours had none of.
    if (scale >= 1.3) {
      this.scorch(px, pz, ground, scale * 0.8)
      this.smoulder(px, pz, ground, scale * 0.75)
    }
  }

  /**
   * The fireball: a size-distributed cube burst.
   *
   * The distribution is the whole effect. `sizeT` is cubed, so most cubes are
   * near the minimum and a few are five or six times that — the few carry the
   * core, the many make the fringe. Reach is then divided by size, so the big
   * ones stay put and the small ones are thrown clear, and the radial lottery is
   * itself squared so density falls off with distance instead of ending at a
   * hard rim. Each cube's ramp offset is raised by how far out and how small it
   * is, which is what produces the hot-centre / cool-shell read in one frame.
   */
  private burst(px: number, pz: number, ground: number, scale: number, ramp: string): void {
    const seed = seedOf(px, pz) ^ 0x51ed270b
    const rampId = this.rampId(ramp)
    const n = clampInt(Math.round(38 * Math.pow(scale, 1.35) * this.density), 6, 150)
    // Half again as wide as it was. A dying tank threw a fireball two thirds of a
    // cell across, which is smaller than the tank — so the frame read as a puff
    // *on* a vehicle instead of a vehicle coming apart. In the reference a
    // detonation is several cells of cloud with wreckage past that, and getting
    // the overall size right does more for the read than any distribution inside
    // it.
    const radius = 0.38 * scale

    for (let i = 0; i < n; i++) {
      const u = rand01(seed, i, 0)
      const sizeT = u * u * u
      // The top of the distribution is bounded at about one radius, which is
      // roughly two thirds of a tank. It used to reach 1.35, and the handful of
      // cubes up there were each wider than the vehicle that died: they welded
      // into a single convex pale mass with the small cubes hidden behind it, so
      // the size distribution existed and could not be seen. More, smaller cubes
      // is the reference's fireball; fewer, larger ones is a cotton ball.
      const size = radius * (0.22 + 0.9 * sizeT)

      const a = rand01(seed, i, 1) * TAU
      const spreadT = rand01(seed, i, 2)
      // The `0.4 +` is the floor on how close to the centre anything may sit,
      // and it is not cosmetic. Without it the reach term goes to zero for the
      // largest cubes — the ones the size law divides hardest — and half a dozen
      // of them stack on the origin at nearly the same ramp step, welding into
      // one flat slab with a hard convex edge. That is the original defect,
      // rebuilt out of the machinery meant to fix it.
      const reach = (0.4 + 2.7 * spreadT * spreadT) / (0.7 + 1.7 * sizeT)
      const dist = radius * reach

      // Upper hemisphere, weighted up: a blast climbs more than it spreads. The
      // floor is shallow on purpose — a symmetric sphere of cubes reads as a
      // ball, and a ball is a prop. The reference's detonations are taller than
      // they are wide, because most of the cloud went up.
      const cosPhi = -0.2 + rand01(seed, i, 3) * 1.2
      const sinPhi = Math.sqrt(Math.max(0, 1 - cosPhi * cosPhi))
      const hx = Math.cos(a) * sinPhi
      const hz = Math.sin(a) * sinPhi

      const speed = dist * 2.4 + 0.35 * scale
      const jitter = rand01(seed, i, 4)
      const reachNorm = Math.min(1, reach / 2.6)

      this.spawn({
        x: px + hx * dist,
        y: Math.max(ground + size * 0.4, ground + 0.16 * scale + cosPhi * dist * 1.2 + size * 0.35),
        z: pz + hz * dist,
        vx: hx * speed,
        vy: cosPhi * speed + 1.15 * scale,
        vz: hz * speed,
        ay: 0.9,
        drag: 2.6,
        // ±35% on the lifetime, so the cloud dissolves unevenly rather than
        // vanishing on one frame.
        //
        // **Scale barely lengthens it any more, and that is the fix for the
        // frame that matters.** At `0.3 + 0.34 * scale` an air strike's flame
        // lived 1.8 s, and every one of these cubes is buoyant — so the shot
        // taken 1.7 s after the blast still had live yellow fire and orange
        // cubes hanging a cell above the ground with the wreckage already
        // settled under them. A bigger blast should be a *wider* fireball, which
        // `radius` and the cube count already give it; it should not be a
        // slower one. Under 0.9 s at every scale means the flame is out well
        // before the smoke thins, which is the order these beats happen in.
        life: (0.32 + 0.19 * scale) * (0.65 + jitter * 0.6),
        size,
        // Growth is a *fraction of the cube's own edge*, never an absolute rate.
        // An absolute one is size-blind: a chip and a chunk both gain the same
        // half-cell per second, so the small end of the distribution — the whole
        // reason the fringe feathers — swells up to meet the large end and the
        // burst turns back into a smooth mass of one size.
        grow: size * 0.25,
        // Collapsing over two thirds of its life rather than under half. A
        // fireball is at full presence for one beat and thinning for the rest of
        // it; holding every cube at full size for 55% of its life is what let a
        // late frame catch a burst that had not visibly started to go out.
        tail: 0.55,
        ramp: rampId,
        // Far and small starts cool; big and central starts white-hot. Two
        // constants are load-bearing here. The offset keeps the white step
        // *rare* — every cube starting at step zero is how the old emitter
        // produced one solid white mass. The ±0.45 jitter keeps neighbours off
        // each other's step: without it, six adjacent large cubes land on the
        // same ramp entry and weld into one flat orange slab with a hard
        // silhouette, which is the same defect the rewrite was for wearing a
        // different colour.
        // The band was 0.04–0.55 before, which spread the cloud over the whole
        // ramp and put most of it in the red half — a fireball whose *average*
        // colour is ember red. It is compressed into the hot third now, and the
        // walk down to smoke is left to age and to the smoke layer above.
        // The `- sizeT` term went from 0.24 to 0.34 to guarantee the top of the
        // size distribution lands *on* step zero rather than near it. White is
        // one twelfth of this ramp, so "near" is a pale yellow cube, and a
        // detonation whose largest block is pale yellow has no core in it — the
        // hits were being read as a pile of gravel at terrain luminance. Only
        // the few cubes with `sizeT` above about 0.7 reach the clamp, which is
        // the handful the reference blows out and not the cloud.
        rampOff: 0.02 + reachNorm * 0.3 + (rand01(seed, i, 5) - 0.5) * 0.3 - sizeT * 0.34,
        // Well under one, because the smoke pass is what carries the cloud to
        // grey. A fireball that walks its whole ramp inside its own short life
        // spends most of that life as ember red, and a battle full of ember red
        // reads as plastic rather than as fire. At 0.55 a central cube goes
        // white → yellow → gold → orange across its whole life and never gets to
        // red at all, which is the reference's core exactly; the fringe still
        // reaches ember because it *started* halfway down the ramp.
        // Nudged to 0.7 now the life is shorter: a core cube reaches orange-red
        // in the frames where it is already collapsing, so the burst *hands off*
        // to the smoke layer instead of winking out mid-gold. The ember steps
        // are still only reached at the very end, which is what keeps a
        // battlefield from reading as red confetti.
        //
        // **Tied to size, so the core cools slower than the fringe.** A single
        // rate walks every cube down the ramp together, so a burst is one colour
        // per frame however carefully the offsets were spread — start it hot and
        // by the third frame the whole thing is gold, which is what every still
        // of an impact in the capture set showed. At `0.62 - 0.28 × sizeT` the
        // big central cubes cross four steps in their whole life (white, pale
        // yellow, gold) while the chips at the fringe still run the full walk to
        // ember and hand off to the smoke. That is the reference's fireball
        // exactly: an overexposed middle that stays overexposed for as long as
        // it exists, inside a shell that is already cooling.
        rampRate: 0.62 - sizeT * 0.28,
        // ±11% of value per cube. The ramp has three identical pale-yellow steps
        // in it by design — that is how it is weighted — so without this a third
        // of the fireball is literally the same number, and the same number over
        // adjacent cubes is one polygon.
        tint: 0.78 + rand01(seed, i, 15) * 0.22,
        spin: 5.5,
        seed,
        index: i,
      })
    }

    // Ejecta: the long tail. These are what stop the burst being a ball.
    //
    // The old version of this layer threw five cubes of 0.05 cells — one pixel
    // each — as far as two thirds of a cell, so the fireball still ended at a
    // radius and the thing meant to break that boundary was invisible. The
    // signature of the reference frame is a *wide* spray of chunks of wildly
    // different sizes, most of them pale rather than burning, thrown four or five
    // times further than the flame reaches and left lying where they land.
    //
    // Two thirds of them are pale and lit, not hot and unlit. Debris a metre out
    // of a blast is not on fire; it is a lump of something with the light of the
    // fire on it, and a spray of glowing sparks reads as fireworks.
    //
    // **Cut back, because the tail had grown longer than the event.** At thirteen
    // per burst thrown out to five radii, a single tank going up covered more
    // screen area in scattered cubes than the two formations fighting over it, and
    // a mid-battle frame read as a snowstorm. Nine cubes over three radii still
    // breaks the fireball's rim — which is the whole job of this layer — without
    // burying the units. The lifetimes are untouched: they are set by what reads
    // in motion, and padding them is how a battlefield fills with litter that
    // never clears.
    const ejecta = clampInt(Math.round(9 * scale * this.density), 4, 32)
    const paleRamp = this.rampId('debris')
    for (let i = 0; i < ejecta; i++) {
      const a = rand01(seed, i, 6) * TAU
      const u = rand01(seed, i, 12)
      // Cubed rather than squared, for the reason written on `debris`: a squared
      // lottery over a 5× band still lands most of the tail within one cube size
      // of the mean, which photographs as a monodisperse speckle. Cubed, the
      // usual piece is a chip and the rare one is a slab.
      const sizeT = u * u * u
      const size = (0.04 + 0.34 * sizeT) * (0.7 + 0.4 * scale)
      // Reach is tied inversely to size here as well: a chip flies, a chunk
      // lands short. Small cubes go out to three radii, which still clears
      // anything the fireball itself covers.
      const spread = (1.0 + rand01(seed, i, 8) * 2.2) / (0.6 + 1.2 * sizeT)
      // **Bounded, and the bound is what stops a match accumulating litter.**
      // `spread` reaches five and `radius` is 0.38 × scale, so a strike-scale
      // blast was *spawning* chips six cells from the crater and then throwing
      // them further. Cubes that far out have no visible connection to the
      // explosion that made them; they read as scenery, and they were turning up
      // in the empty third of frames where nothing had ever been fighting. A few
      // tiles is the whole budget.
      const out = Math.min(radius * spread, 0.9 + 0.45 * scale)
      const speed = (1.8 + rand01(seed, i, 7) * 2.6) * Math.pow(scale, 0.4)
      // A quarter burning, three quarters not. Higher than this and the spray
      // reads as fireworks: a hot cube that lands and lies there is a red block
      // on the dirt, and a dozen of them is confetti rather than wreckage.
      const hot = rand01(seed, i, 14) < 0.25
      this.spawn({
        x: px + Math.cos(a) * out,
        y: ground + 0.18 * scale + rand01(seed, i, 9) * 0.7 * scale,
        z: pz + Math.sin(a) * out,
        vx: Math.cos(a) * speed,
        vy: 1.4 + rand01(seed, i, 10) * 1.9,
        vz: Math.sin(a) * speed,
        ay: GRAVITY * (hot ? 0.45 : 1),
        // Lateral travel is roughly `speed / drag`, so this is the second half
        // of the bound above: at 0.4 a chip coasted for its whole life and the
        // spawn cap would have been decoration. At 2.2 it covers a cell or so
        // past where it started and stops.
        drag: 2.2,
        // Rests on the deck. A cube that keeps falling through the terrain is
        // gone in half a second and the ground is spotless again; the reference's
        // ground is *littered* long after the flame is out.
        rest: size * 0.45,
        // The cold half was 1.4–3.0 s with a `tail` of 0.18, which is 3 s of
        // full-size cube and a third of a second of dissolve — so the frame at
        // +1.7 s caught every chip of every blast at spawn size and full value.
        // Halved, and the dissolve tripled: what survives to the settled frame
        // is a handful of small pieces visibly on their way out.
        //
        // Under 1.6 s now, and dissolving over most of it. The ejecta is the
        // *fringe* of a burst — its job is to break the fireball's rim on the
        // frame it appears — and a fringe that is still lying around when the
        // smoke has gone is litter rather than a blast. `debris` is the layer
        // that carries wreckage past the event.
        life: hot ? 0.3 + rand01(seed, i, 11) * 0.35 : 0.85 + rand01(seed, i, 11) * 0.7,
        size,
        grow: -0.01,
        tail: hot ? 0.5 : 0.6,
        ramp: hot ? rampId : paleRamp,
        // The pale offsets straddle the debris ramp's first *two* steps rather
        // than sitting on the first. A capture is taken a fraction of a second
        // after the burst, so every chunk that starts at step zero is still there
        // — and a dozen cubes on one identical pale step is the flat field of
        // confetti, whatever the ramp does later. Spread across two steps the
        // same cubes read as wreckage of mixed material.
        rampOff: hot ? 0.05 + rand01(seed, i, 13) * 0.2 : 0.06 + rand01(seed, i, 13) * 0.34,
        // A burning chip goes out on the way down rather than glowing on the
        // ground; a pale one darkens and takes on the ground's tint as it settles,
        // which is what stops the deck being littered with bright chips. At 0.35
        // a chunk walked barely one step of its ramp and stayed the colour it
        // spawned; 0.8 carries it from concrete grey to scorch by the time it
        // stops moving.
        rampRate: hot ? 1.6 : 0.8,
        // Wreckage is not one material. The debris ramp has four steps and a
        // dozen chunks land on two of them, so the value jitter is what stops
        // the spray reading as a set of identical parts.
        tint: 0.78 + rand01(seed, i, 15) * 0.22,
        solid: !hot,
        spin: 11,
        seed,
        index: 40 + i,
      })
    }
  }

  /**
   * The overexposed core: a handful of unlit white cubes, gone in four ticks.
   *
   * Every other layer in a detonation is a *distribution* — sizes, reaches and
   * ramp offsets spread so the cloud has structure — and the cost of that is
   * that no single cube is ever the brightest thing on screen. A burst caught
   * past its first beat is therefore gold and grey at the same luminance as the
   * terrain, which is the "bag of gravel dropped on the ground" read: the hits
   * had colour and volume and no *event* in them.
   *
   * This is the event. Larger than anything in the burst, on a ramp that is
   * white for most of its length, and dead inside 0.15 s — which is short on
   * purpose. It is not here to be photographed; it is here so a fight watched
   * at speed has a beat of overexposure at every impact, the way the reference
   * does. What a still frame catches is the fireball, the ground ring and the
   * plume, all of which already outlive it several times over.
   *
   * Deliberately *not* a point light. Both scene lights are neutral and fixed by
   * §9, and a warm flicker added here would multiply each channel differently —
   * an off-palette pixel everywhere it reached. `glow` already paints the spill
   * on the deck instead, which is the same read inside the rules.
   */
  private flash(px: number, pz: number, ground: number, scale: number): void {
    const seed = seedOf(px, pz) ^ 0x6b43a9f5
    const n = clampInt(Math.round(7 * this.density), 5, 10)
    const rampId = this.rampId('flash')
    for (let i = 0; i < n; i++) {
      const a = rand01(seed, i, 0) * TAU
      const out = 0.24 * scale * rand01(seed, i, 1)
      // Two to three times the fireball's own largest cube. A flash the size of
      // the burst hides inside it and is simply a whiter cube among gold ones.
      const size = 0.5 * scale * (0.6 + rand01(seed, i, 2) * 0.9)
      this.spawn({
        x: px + Math.cos(a) * out,
        y: ground + 0.18 * scale + rand01(seed, i, 3) * 0.4 * scale,
        z: pz + Math.sin(a) * out,
        life: 0.1 + rand01(seed, i, 4) * 0.05,
        size,
        grow: -size * 0.6,
        // Collapsing from the first frame, so the core is at its widest on the
        // tick it appears and never lingers as a white blob.
        tail: 1,
        ramp: rampId,
        rampOff: 0,
        rampRate: 0.9,
        spin: 4,
        seed,
        index: 220 + i,
      })
    }
  }

  /**
   * Light thrown on the ground by a detonation, painted rather than lit.
   *
   * Both scene lights are neutral by rule (§9) and a warm point light would
   * multiply every channel differently, so the only way to put the flash of an
   * explosion onto the terrain around it is to draw it: a scatter of near-flat
   * unlit slabs on the deck, walking their ramp two and a half times faster than
   * anything else so they go white, gold, orange, gone inside a third of a
   * second. That rate is the whole trick — a glow that cools slowly is a paint
   * stain, and one that cools fast is a flash.
   */
  private glow(px: number, pz: number, ground: number, scale: number, ramp: string): void {
    const seed = seedOf(px, pz) ^ 0x39a1c07b
    const n = clampInt(Math.round(5 * this.density), 3, 10)
    const rampId = this.rampId(ramp)
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU + rand01(seed, i, 0) * 0.7
      const out = 0.4 * scale * rand01(seed, i, 1)
      const size = 0.62 * scale * (0.5 + rand01(seed, i, 2) * 0.9)
      this.spawn({
        x: px + Math.cos(a) * out,
        y: ground + 0.045,
        z: pz + Math.sin(a) * out,
        life: 0.26 + rand01(seed, i, 3) * 0.26,
        size,
        // Squashed flat and laid at a yaw rather than tumbling: this is light on
        // the deck, and light on the deck does not somersault.
        flat: 0.07,
        tail: 0.8,
        ramp: rampId,
        rampOff: 0.01 + rand01(seed, i, 4) * 0.12,
        rampRate: 1.5,
        yaw: a,
        seed,
        index: i,
      })
    }
  }

  /**
   * The ground ring: dust punched outward along the terrain by the blast.
   *
   * Spawned already at radius, low and flat, travelling out. It is what tells a
   * viewer the explosion touched the ground rather than floating over it, and it
   * is drawn as a ring of separate cubes rather than a quad because a smooth
   * expanding disc is the one shape in this game that would look imported.
   *
   * **It is a footprint, not a puff, and that is the change.** These were small
   * tumbling cubes at a third of the outward speed, which at this zoom is a few
   * specks lost in the ejecta — so a detonation had a fireball, a plume and
   * nothing at all connecting it to the terrain. Wider, flattened onto the deck,
   * pinned to a yaw instead of somersaulting, and thrown out three times as
   * fast, the same layer reads as the shockwave crossing the ground. Flat is
   * what keeps it cheap: a slab a third of a cube's height covers screen area
   * without adding volume, so the ring reads at a glance and still cannot be
   * mistaken for wreckage standing beside the wreck.
   */
  private ring(px: number, pz: number, ground: number, scale: number): void {
    const seed = seedOf(px, pz) ^ 0x2f9e3d17
    const n = clampInt(Math.round(13 * scale * this.density), 8, 26)
    const rampId = this.rampId('dust')
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU + rand01(seed, i, 0) * 0.5
      const out = 0.5 * scale * (0.8 + rand01(seed, i, 1) * 0.7)
      // Wider than it was, and it can afford to be because it is a slab now.
      // The old note here is still the binding one — a ring cube grows for most
      // of a second, so it is the *top* of this distribution that reaches the
      // screen — which is why the growth rate came down as the size went up.
      const size = 0.13 * scale * (0.5 + rand01(seed, i, 2) * 1.1)
      this.spawn({
        x: px + Math.cos(a) * out,
        y: ground + size * 0.2,
        z: pz + Math.sin(a) * out,
        vx: Math.cos(a) * 4.6 * scale,
        vy: 0.35,
        vz: Math.sin(a) * 4.6 * scale,
        ay: -1.2,
        drag: 3.2,
        life: 0.45 + rand01(seed, i, 3) * 0.4,
        size,
        grow: size * 0.4,
        // Thinning the whole way out, so the ring dissipates as it expands
        // rather than reaching its widest and then dropping out on one frame.
        tail: 0.8,
        // Squashed onto the terrain. A ring of tumbling cubes is more ejecta;
        // a ring of low slabs is the blast touching the ground.
        flat: 0.32,
        ramp: rampId,
        rampOff: rand01(seed, i, 4) * 0.35,
        solid: true,
        yaw: a,
        seed,
        index: i,
      })
    }
  }

  /**
   * The column that outlives the flash.
   *
   * Part of the column is placed up its own height at spawn rather than all of
   * it released from the base: the plume has to read as a plume on the frame it
   * appears, not only two seconds later. The rest of the climb is real — it
   * rises, spreads and darkens as it ages.
   *
   * Sizes are small-biased and the growth is relative, both hard-won. Smoke is
   * the longest-lived thing in the pool, so an absolute growth rate compounds
   * for three or four seconds and turns a plume into a stack of boulders
   * standing over the battlefield.
   */
  private smoke(px: number, pz: number, ground: number, scale: number, count: number): void {
    const n = clampInt(count, 0, 56)
    if (n === 0) return
    const seed = seedOf(px, pz) ^ 0x68e31da4
    const rampId = this.rampId('smoke')
    for (let i = 0; i < n; i++) {
      const t = (i + rand01(seed, i, 0)) / n
      const a = rand01(seed, i, 1) * TAU
      const u = rand01(seed, i, 3)
      // The same cubic law the fireball uses, and for the same reason. At
      // `0.4 + 1.1u²` the whole plume sat inside a 3.7× band, which at this zoom
      // is one cube size — so the cloud had a boundary as dense and as solid as
      // its middle and read as a packed clump of identical boxes. Cubed, most
      // cubes are near the floor and a few are seven times that: the few are the
      // body of the plume and the many are what it frays into.
      const sizeT = u * u * u
      const size = 0.13 * scale * (0.35 + 2.1 * sizeT) * (0.7 + t * 0.4)
      // Reach is squared *and* tied inversely to size, so density falls off with
      // radius and the outer shell is the small cubes. A perimeter made of the
      // same blocks as the core is a silhouette; a perimeter made of chips is a
      // cloud thinning out.
      const spread = rand01(seed, i, 2)
      const out = (0.3 * scale * (0.25 + 1.9 * spread * spread) * (0.5 + t)) / (0.65 + 1.1 * sizeT)
      // Long enough that "the fire is out and the smoke is still there" is a beat
      // — an impact and a wreck must not look identical two seconds apart — and
      // short enough that a single blast is *done*.
      //
      // **The scale term is gone, and that is the fix for the settled frame.**
      // At `× (0.8 + 0.16 × scale)` a strike-scale plume ran to 4.4 s, so at
      // +1.7 s its cubes were only two fifths through their own dissolve: forty
      // near-white blocks at better than half size, which is not a plume
      // dissipating, it is an explosion paused. A bigger blast should throw a
      // *wider* column — `scale` already drives the count, the sizes and the
      // spread — and not a slower one. Capped at 2.5 s, the shot at +1.7 s
      // catches the survivors past three quarters of their life, which is the
      // thin grey wisp the reference leaves.
      //
      // Trimmed again, to 1.3–2.2 s. At 1.4–2.5 the survivors were still a third
      // of the way from the top of their dissolve when `air-strike-settled` is
      // taken, and a line of blasts along a strike corridor put forty of them
      // across the frame at half size and near-white — the "fresh plume still
      // rising" the settled shot is scored on. The column still outlives the
      // flame it stands over by three times, which is the beat it exists for.
      const life = 1.3 + rand01(seed, i, 4) * 0.9
      this.spawn({
        // The lateral offset is per cube, not a function of height. It was
        // `t × 0.3 × scale` with a matching uniform `vx`, so every cube in every
        // plume leaned the same way by the same amount and a row of impacts
        // welded into one hard diagonal rope of gravel instead of a row of
        // clouds. A little shared drift is wind; all of it is a conveyor belt.
        x: px + Math.cos(a) * out + t * 0.12 * scale + (rand01(seed, i, 8) - 0.5) * 0.55 * scale,
        y: ground + 0.2 * scale + t * 1.05 * scale,
        z: pz + Math.sin(a) * out + (rand01(seed, i, 9) - 0.5) * 0.4 * scale,
        vx: 0.12 * scale,
        vy: 0.5 + rand01(seed, i, 5) * 0.35,
        ay: 0.12,
        drag: 0.55,
        life,
        size,
        // Down from 0.3 now that the column lives twice as long: growth is a
        // fraction of the cube's own edge *per second*, so doubling the life
        // doubles what it compounds to, and a plume of boulders is the failure
        // this rate exists to avoid.
        grow: size * 0.18,
        // Dissipating from the first frame, the way the wreck's own column
        // already does. `tail` is the fraction of life spent collapsing, so at
        // 0.5 every cube held full size — still growing — for half its life, and
        // half of four seconds is exactly when the settled shot is taken.
        tail: 0.95,
        ramp: rampId,
        // Higher in the column is older, so it is already darker. The offset is
        // what makes a still frame show a plume that shades from pale at the
        // fire to sooty at the top.
        rampOff: t * 0.28 + rand01(seed, i, 6) * 0.12,
        // Slower than the default, because this pass is lit and every step down
        // the ramp is *also* multiplied by the shading on each face — but 0.4
        // was slow enough that a plume was still near-white when the fire under
        // it had been out for a second, which is what made the aftermath read as
        // suspended rubble. At 0.65 the column is white blocks while the flame
        // burns and grey by the time it thins, which is the order the reference
        // does it in.
        //
        // 0.9, so what is left at the end of the life is soot rather than a pale
        // block: a near-white cube shrinking is still a *bright* cube, and forty
        // of them is the frame reading as unfinished however small they are.
        rampRate: 0.9,
        // ±11%, so the mass is grey cubes rather than one grey. The pass is lit,
        // which already varies value across the three faces of any one cube, but
        // every cube in a plume shows the *same* three — the jitter is what
        // separates neighbours.
        tint: 0.78 + rand01(seed, i, 7) * 0.22,
        solid: true,
        // No individual shadow, for the reason written on the pool field: a
        // plume hangs at height by design, and a hard shadow under each of its
        // cubes is what makes them read as suspended rubble. Every other smoke
        // emitter in this file already sets it; this one, the largest, did not.
        shadowless: true,
        spin: 0.7,
        seed,
        index: i,
      })
    }
  }

  /**
   * The mark. Flat dark blocks laid on the ground, living tens of seconds.
   *
   * A battlefield that returns to pristine the instant the flash ends is the
   * single loudest tell that these are decals rather than events. This is the
   * cheapest possible fix: a few near-flat cubes, no motion, no ramp movement,
   * shrinking away over ten seconds.
   */
  private scorch(px: number, pz: number, ground: number, scale: number): void {
    const seed = seedOf(px, pz) ^ 0x1b873593
    const n = clampInt(Math.round(3 * this.density), 1, 6)
    const rampId = this.rampId('scorch')
    for (let i = 0; i < n; i++) {
      const a = rand01(seed, i, 0) * TAU
      // Spread wider than it was. At 0.3 the slabs stacked almost on top of each
      // other, so a strike that threw a plume across four cells left a mark
      // barely wider than one — the aftermath frame had a crater with no crater
      // under it. Offsetting to 0.45 turns the same few slabs into a patch the
      // size of the blast that made it.
      const out = 0.45 * scale * rand01(seed, i, 1)
      const size = 0.55 * scale * (0.5 + rand01(seed, i, 2) * 0.9)
      this.spawn({
        x: px + Math.cos(a) * out,
        y: ground + 0.03,
        z: pz + Math.sin(a) * out,
        life: 9 + rand01(seed, i, 3) * 5,
        size,
        // Flat: a scorch is a stain, so the box is squashed to a slab that sits
        // just clear of the terrain rather than z-fighting with it.
        flat: 0.055,
        tail: 0.3,
        ramp: rampId,
        rampOff: rand01(seed, i, 4) * 0.5,
        rampRate: 0,
        solid: true,
        yaw: a,
        seed,
        index: i,
      })
    }
  }

  /**
   * What is still burning ten seconds later.
   *
   * A fight in the reference frames leaves *hulks*: low flame guttering on the
   * deck with a thin column standing over it, for far longer than the detonation
   * itself lasted. Ours had the middle beat and nothing either side of it — the
   * same battlefield read as pristine three seconds after a dozen vehicles died
   * on it, because every layer was keyed to the flash.
   *
   * A dead unit is gone from the simulation, so there is nothing to attach an
   * emitter to and nothing that may be invented here to attach one to (§6.2:
   * presentation reads state, never adds to it). The wreck's own smoulder is
   * therefore *pre-laid* at the moment of death: a stack of embers and a column
   * of smoke, each cube given the position, size and ramp offset it would have
   * had if it had been emitted over the intervening seconds.
   */
  private smoulder(px: number, pz: number, ground: number, scale: number): void {
    const seed = seedOf(px, pz) ^ 0x5bd1e995
    const embers = clampInt(Math.round(4 * this.density), 2, 8)
    const fireRamp = this.rampId('fire')
    for (let i = 0; i < embers; i++) {
      const a = rand01(seed, i, 0) * TAU
      const out = 0.3 * scale * rand01(seed, i, 1)
      const size = 0.1 * scale * (0.6 + rand01(seed, i, 2) * 0.9)
      this.spawn({
        x: px + Math.cos(a) * out,
        y: ground + size * 0.6 + rand01(seed, i, 3) * 0.2 * scale,
        z: pz + Math.sin(a) * out,
        vy: 0.35,
        // Barely buoyant, where it used to climb. Terminal rise is `ay / drag`,
        // so at 0.5 an ember spent four seconds drifting a cell and a half off
        // the deck: a flame that leaves the wreck it belongs to is a floating
        // orange cube, which is precisely what the settled frame caught. Flame
        // licks up off a hulk and stays with it.
        ay: 0.22,
        drag: 1.4,
        // Staggered lifetimes, so the hulk goes out cube by cube instead of all
        // at once — the guttering is the difference between fire and a lamp.
        life: 1.2 + rand01(seed, i, 4) * 2.2,
        size,
        grow: -size * 0.12,
        tail: 0.7,
        ramp: fireRamp,
        // Deep in the ramp: this is an ember, not a detonation. Starting hot
        // would put a white-hot core on a wreck that stopped exploding seconds
        // ago, which reads as a second explosion rather than as an aftermath.
        rampOff: 0.34 + rand01(seed, i, 5) * 0.22,
        rampRate: 0.5,
        spin: 2,
        seed,
        index: 200 + i,
      })
    }
    const column = clampInt(Math.round(5 * this.density), 2, 10)
    const smokeRamp = this.rampId('smoke')
    for (let i = 0; i < column; i++) {
      const t = (i + rand01(seed, i, 6)) / column
      const size = 0.13 * scale * (0.5 + t * 0.8)
      this.spawn({
        x: px + (rand01(seed, i, 7) - 0.5) * 0.4 * scale + t * 0.5 * scale,
        // Half the height it was. `t × 2.1 × scale` put the top of a strike's
        // column three and a half cells up, and a cube that high with clear air
        // under it has nothing tying it to the wreck it came off — in the
        // settled frame it is simply floating. A column that stays close to its
        // own hulk reads as the hulk still burning.
        y: ground + 0.35 * scale + t * 1.15 * scale,
        z: pz + (rand01(seed, i, 8) - 0.5) * 0.4 * scale,
        vx: 0.3,
        vy: 0.45,
        ay: 0.08,
        drag: 0.4,
        // Down from 3–7 s. The beat this layer exists for is "the fight left
        // something behind", and two to four seconds of thinning column carries
        // it; seven seconds means the wreck of the *previous* engagement is
        // still smoking through the next one.
        life: 2.2 + rand01(seed, i, 9) * 2.2,
        size,
        grow: size * 0.16,
        // 0.85, not 0.5: a plume must thin out the whole time it exists.
        //
        // `tail` is the fraction of life spent dissolving, so 0.5 held a cube at full
        // size — still growing, since `grow` is positive — for half a lifetime and then
        // collapsed it. On a three-to-seven second plume that put the smoke at maximum
        // presence 1.7s after the strike, which is exactly when `air-strike-settled` is
        // captured, and is why four juries read it as cubes hanging in the air. Cutting
        // the lifetime was the wrong lever — the plume should *last*, and dissipate.
        tail: 0.85,
        ramp: smokeRamp,
        // Starts a third of the way down the ramp, not a sixth. This column is
        // the *aftermath* — it is pre-laid at the moment of death and outlives
        // the flame by seconds — so a wreck whose smoke is still at the white
        // anchor when the fire under it is out is the frame that reads as an
        // explosion someone paused. Grey at the deck, soot at the top.
        rampOff: 0.3 + t * 0.4,
        rampRate: 0.5,
        solid: true,
        shadowless: true,
        spin: 0.5,
        seed,
        index: 210 + i,
      })
    }
  }

  /**
   * A unit coming apart into the blocks it was built from.
   *
   * The models are assembled from boxes, so when one dies it should scatter
   * boxes — thrown outward and upward, tumbling down under gravity and landing
   * where they fall. An explosion alone reads as a puff of light with the unit
   * simply deleted underneath it; debris reads as the thing having been *made*
   * of something.
   *
   * Coloured from the owner's own ramp rather than from the fire palette, so
   * the wreckage is recognisably the unit that just died and, briefly, whose it
   * was — with a few pieces darkened to burnt so the scatter is not one flat
   * shade of the player colour.
   */
  debris(x: fix.Fixed, y: fix.Fixed, ground: number, colour: [number, number, number], scale = 1): void {
    const px = fix.toFloat(x)
    const pz = fix.toFloat(y)
    const seed = seedOf(px, pz) ^ 0x27d4eb2f
    const count = clampInt(Math.round(DEBRIS_PIECES * scale * this.density), 4, 48)
    const paleRamp = this.rampId('debris')
    for (let i = 0; i < count; i++) {
      const a = rand01(seed, i, 0) * TAU
      const u = rand01(seed, i, 1)
      // **Cubed, like the fireball, and it was squared over a 3.7× band.** That
      // band is one cube size at this zoom, so every piece a unit came apart into
      // was the same piece: a monodisperse speckle spread evenly over a wide
      // area, which is the single loudest difference between our blasts and the
      // reference's. Cubed over 8× most pieces are chips and one or two are
      // slabs a quarter the width of the hull they came off, which is what the
      // reference throws.
      const sizeT = u * u * u
      const size = (0.07 + 0.5 * sizeT) * scale
      // Barely scaled by `scale` any more, and drag added below. A structure
      // dying at scale 1.8 threw pieces at nearly seven cells a second through
      // a drag of 0.25, which is a piece of hull crossing half a screen — that
      // is where the stray cubes tens of tiles from any fighting came from.
      //
      // Divided by size, the same way the fireball ties reach to it: a slab
      // drops beside the wreck and a chip flies. Without that the scatter has
      // uniform density out to a hard rim — no dense core, no sparse tail.
      const speed = ((0.9 + rand01(seed, i, 2) * 1.8) * (0.8 + scale * 0.2)) / (0.6 + 1.6 * sizeT)
      const rise = 1.2 + rand01(seed, i, 3) * 1.8
      // **Under one and a quarter seconds, and the split below is why it can
      // be.** Two to three and a half was still long enough that a shot taken
      // 1.7 s after a strike caught the whole scatter — every piece at spawn
      // size, still pale, a third of them mid-arc, which is the "explosion
      // paused" the settled frame keeps being marked down for. Shortening it
      // used to cost the thing the layer is *for*, because this was also the
      // only wreckage the battlefield ever got; the settled pass below now
      // carries that, so the thrown pieces can be what they actually are — a
      // second of matter in the air — and nothing this emitter throws is still
      // aloft when the fire is out.
      const life = 0.75 + rand01(seed, i, 4) * 0.45
      // Placed partway along the arc it is on, because a screenshot is taken at
      // dt = 0 and everything spawned at the centre stacks into one lump there.
      const t = rand01(seed, i, 5) * 0.35
      // Half the scatter is burnt rather than painted. A wreck made entirely of
      // the owner's colour reads as confetti in that colour; the burnt pieces are
      // what make it read as something that was on fire a second ago.
      //
      // **Burnt means the `debris` ramp, not one fixed grey.** It used to be a
      // single palette ash entry — so half of every wreck was literally the same
      // number, and a strike that killed six units handed the frame a hundred
      // identical pale cubes. The ramp is four steps from concrete to scorch and
      // it walks with age, so the burnt half is a spread of materials that cools
      // to the colour of the ground it is lying on.
      const burnt = rand01(seed, i, 6) < 0.5
      // ±17%, the same band every other emitter's `tint` uses, rather than
      // 0.6–1.2. The bottom of that range halved an already-dark hull colour and
      // then the lit pass halved the shaded faces again, which is how a scatter
      // of navy wreckage came out as black squares on the dirt.
      const shade = 0.83 + rand01(seed, i, 7) * 0.34
      const [r, g, b] = colour
      this.spawn({
        x: px + Math.cos(a) * (0.18 * scale + speed * t),
        y: ground + 0.25 + rise * t + GRAVITY * 0.5 * t * t,
        z: pz + Math.sin(a) * (0.18 * scale + speed * t),
        vx: Math.cos(a) * speed,
        vy: rise + GRAVITY * t,
        vz: Math.sin(a) * speed,
        ay: GRAVITY,
        // 2.6, not 1.6. `rest` is the ground height under the *death*, which is
        // the only one this emitter is given, so a chip that coasts two or three
        // cells across a slope comes to rest at the wrong elevation and hangs
        // there — cubes stopped in mid-air over lower ground, which is exactly
        // what the settled frame photographed. Enough drag to keep the scatter
        // over the tile it came off keeps the landing honest.
        drag: 2.6,
        // Lands on the ground it was thrown off and stays there. A piece of
        // wreckage lying in the dirt is worth more to the frame than the second
        // it spent in the air.
        rest: size * 0.45,
        life,
        size,
        grow: -0.01,
        // Collapsing over most of its life rather than the last fifth. `tail` is
        // the only dissolve this renderer has — one shared material means one
        // opacity, so a cube fades by shrinking — and at 0.22 a piece sat at
        // spawn size for nearly three seconds and then dropped out. That is the
        // "nothing has settled" read: a field of cubes at full presence long
        // after the event, then a frame where they vanish.
        tail: 0.7,
        r: Math.min(1, r * shade),
        g: Math.min(1, g * shade),
        b: Math.min(1, b * shade),
        ramp: burnt ? paleRamp : 0,
        rampOff: rand01(seed, i, 8) * 0.4,
        rampRate: 0.6,
        tint: burnt ? shade : 1,
        solid: true,
        spin: 7,
        seed,
        index: 100 + i,
      })
    }

    // The hulk. What the fight leaves on the map once everything thrown by it
    // has landed and gone out.
    //
    // This is the half of the old debris layer that was worth keeping, split
    // out so the other half could be shortened. A wreck is not a scatter of
    // spawn-sized pale chips frozen where they fell — it is a few low lumps
    // sitting *on* the ground, half the size of anything that was thrown, dark
    // enough to belong to the terrain rather than to sit on top of it. Placed at
    // rest with no velocity, because these never travelled: the pieces that
    // travelled are already gone by the time these matter.
    //
    // Long-lived, and that is not a padded lifetime — nothing here is airborne,
    // nothing here is bright, and it lives on the same clock as the scorch it
    // sits inside. The battlefield keeping a record of what died on it is the
    // single thing the reference frames have most of.
    const settled = clampInt(Math.round(6 * this.density), 4, 8)
    for (let i = 0; i < settled; i++) {
      const a = rand01(seed, i, 16) * TAU
      const out = (0.2 + rand01(seed, i, 17) * 0.55) * scale
      const size = (0.08 + 0.15 * rand01(seed, i, 18)) * scale
      this.spawn({
        x: px + Math.cos(a) * out,
        y: ground + size * 0.42,
        z: pz + Math.sin(a) * out,
        rest: size * 0.42,
        life: 7 + rand01(seed, i, 19) * 4,
        size,
        tail: 0.45,
        ramp: paleRamp,
        // The bottom two steps of the debris ramp — mid grey and the scorch
        // colour — never the concrete white it starts on. A hulk that reads as
        // pale is litter; one that reads as burnt is history.
        rampOff: 0.56 + rand01(seed, i, 20) * 0.3,
        rampRate: 0.3,
        // Darker than any other emitter's band, on purpose. The lit pass puts a
        // top face at nearly full albedo, and a settled lump has to sit *under*
        // the value of the ground around it or the frame reads as littered.
        tint: 0.66 + rand01(seed, i, 21) * 0.18,
        solid: true,
        // Random orientation, no tumble: it came to rest a long time ago.
        spin: 0,
        seed,
        index: 130 + i,
      })
    }
  }

  /** A structure coming apart: a bigger, slower, dirtier version. */
  wreck(x: fix.Fixed, y: fix.Fixed, ground: number): void {
    const px = fix.toFloat(x)
    const pz = fix.toFloat(y)
    this.burst(px, pz, ground, 2.4, 'fire')
    this.flash(px, pz, ground, 2.6)
    this.glow(px, pz, ground, 2.6, 'fire')
    this.ring(px, pz, ground, 2.6)
    this.smoke(px, pz, ground, 2.4, Math.round(30 * this.density))
    this.scorch(px, pz, ground, 2.6)
    this.smoulder(px, pz, ground, 2.2)
  }

  /**
   * Ordnance leaving the rack: a puff at the release point.
   *
   * The bomb itself is a simulation projectile and `tracer` draws it; this is
   * only the moment of separation, which is otherwise the one part of an air
   * strike with nothing at all attached to it.
   */
  release(x: fix.Fixed, y: fix.Fixed, ground: number, height: number): void {
    const px = fix.toFloat(x)
    const pz = fix.toFloat(y)
    const seed = seedOf(px, pz) ^ 0x7feb352d
    const rampId = this.rampId('smoke')
    const n = clampInt(Math.round(6 * this.density), 2, 10)
    for (let i = 0; i < n; i++) {
      const t = i / n
      const a = rand01(seed, i, 0) * TAU
      this.spawn({
        x: px + Math.cos(a) * 0.12,
        y: ground + height - t * 0.55,
        z: pz + Math.sin(a) * 0.12,
        vy: -0.4,
        ay: 0.6,
        drag: 1.6,
        life: 0.5 + rand01(seed, i, 1) * 0.4,
        size: 0.16 * (1 - t * 0.6),
        grow: 0.2,
        tail: 0.6,
        ramp: rampId,
        rampOff: 0.15 + t * 0.4,
        solid: true,
        shadowless: true,
        spin: 1.5,
        seed,
        index: i,
      })
    }
  }

  /**
   * An aircraft's exhaust: a hot nozzle and a short trail of smoke behind it.
   *
   * The whole trail is laid down at once, pre-distributed backwards along the
   * heading, rather than dropped one puff per frame. Two reasons, and both are
   * the same reason: a screenshot is taken at `dt = 0`, and a per-frame emitter
   * ties trail density to frame rate. Laying the trail spatially means a gunship
   * has a trail in a still frame and the same trail on every machine.
   *
   * Called on a tick stagger, not per frame — four aircraft emitting every frame
   * would own the pool.
   */
  exhaust(x: number, z: number, y: number, ground: number, heading: number, index: number): void {
    const seed = seedOf(x, z) ^ 0x3b9aca07
    const bx = -Math.cos(heading)
    const bz = -Math.sin(heading)
    this.spawn({
      x: x + bx * 0.2,
      y,
      z: z + bz * 0.2,
      vx: bx * 0.5,
      vz: bz * 0.5,
      life: 0.24,
      size: 0.15,
      grow: -0.3,
      tail: 0.6,
      ramp: this.rampId('fire'),
      rampOff: 0.12,
      spin: 4,
      seed,
      index,
    })
    const rampId = this.rampId('smoke')
    // The trail's own ground-plane perpendicular. Five puffs of near-identical
    // size laid in a dead-straight line at even spacing is the "hard gravel
    // rope" the strike frames photographed — four gunships flying abreast wrote
    // four ropes of pale blocks across the battlefield, and a rope is the one
    // thing smoke is not. Scattering them off the axis and spreading the sizes
    // on the same cubic law every other emitter uses turns each rope back into a
    // wake.
    const nx = -bz
    const nz = bx
    for (let i = 0; i < 5; i++) {
      const d = 0.4 + i * 0.5
      const u = rand01(seed, i, 10)
      const size = (0.05 + 0.3 * u * u * u) * (1 + i * 0.22)
      const off = (rand01(seed, i, 11) - 0.5) * (0.24 + i * 0.12)
      this.spawn({
        x: x + bx * d + nx * off,
        y: y - 0.05 * i + (rand01(seed, i, 12) - 0.5) * 0.24,
        z: z + bz * d + nz * off,
        vx: bx * 0.35,
        vy: -0.12,
        vz: bz * 0.35,
        ay: 0.2,
        drag: 1.2,
        life: 0.5 + i * 0.16,
        size,
        grow: size * 0.5,
        // Thinning from the first frame. At 0.6 a puff held full size for the
        // two fifths of its life the camera is most likely to catch it in.
        tail: 0.85,
        ramp: rampId,
        rampOff: 0.08 + i * 0.15 + rand01(seed, i, 3) * 0.1,
        rampRate: 0.6,
        solid: true,
        // Exhaust is smoke, and it is emitted at flight altitude: given a hard
        // individual shadow per cube it is the exact failure the pool field
        // warns about — cubes suspended in mid-air, each one dropping its own
        // shadow on the deck. Every other smoke emitter here already sets this.
        shadowless: true,
        tint: 0.78 + rand01(seed, i, 13) * 0.22,
        spin: 1,
        seed,
        index: index * 8 + i,
      })
    }
    // Downwash. The rotors are pushing on the ground a cell and a half below,
    // and the dust they kick up is the only part of a low pass that touches the
    // terrain — without it an aircraft is a model with nothing under it.
    const dust = this.rampId('dust')
    for (let i = 0; i < 3; i++) {
      const a = rand01(seed, i, 5) * TAU
      const out = 0.3 + rand01(seed, i, 6) * 0.45
      const size = 0.08 + rand01(seed, i, 7) * 0.1
      this.spawn({
        x: x + Math.cos(a) * out,
        y: ground + size * 0.5,
        z: z + Math.sin(a) * out,
        vx: Math.cos(a) * 1.1,
        vy: 0.15,
        vz: Math.sin(a) * 1.1,
        ay: -0.8,
        drag: 2.6,
        life: 0.35 + rand01(seed, i, 8) * 0.25,
        size,
        grow: 0.35,
        tail: 0.65,
        ramp: dust,
        rampOff: 0.15 + rand01(seed, i, 9) * 0.35,
        solid: true,
        spin: 2,
        seed,
        index: index * 8 + 5 + i,
      })
    }
  }

  /**
   * Something wounded, burning. Called on a slow cadence, not per frame.
   *
   * `heat` in [0, 1] is how badly hurt it is: a unit at a third health gets a
   * wisp, a structure about to fall gets flame with smoke over it. Driving the
   * emitter from health rather than from a flag is what makes a firing line
   * visibly degrade instead of switching from clean to burning in one frame.
   */
  fire(x: number, z: number, ground: number, index: number, heat = 1, scale = 1): void {
    const seed = seedOf(x, z) ^ (index * 0x9e3779b1)
    const a = index * 2.399
    const spread = 0.3 * scale
    // A *cluster*, not a puff. One cube every eleventh tick with a half-second
    // life leaves roughly one flame cube alive per burning vehicle, and one cube
    // is not a fire — the reference's burning tank wears a knot of orange blocks
    // about as big as the tank itself. Three per pass at a third again the size
    // is what reads at this zoom, and it is still under twenty cubes for a
    // burning base.
    const rampId = this.rampId('fire')
    for (let k = 0; k < 3; k++) {
      const ka = a + k * 2.1
      const kout = spread * (0.35 + k * 0.4)
      this.spawn({
        x: x + Math.cos(ka) * kout,
        y: ground + (0.3 + k * 0.16) * scale + rand01(seed, index, k) * 0.2,
        z: z + Math.sin(ka) * kout,
        vy: 0.8 + rand01(seed, index, 1) * 0.4,
        ay: 0.8,
        drag: 1.2,
        life: 0.4 + rand01(seed, index, 2) * 0.4,
        size: (0.13 + rand01(seed, index, 3 + k) * 0.17) * scale * (0.6 + heat * 0.6),
        grow: 0.2,
        tail: 0.5,
        ramp: rampId,
        // Hotter at the base of the knot and cooling upward, so a burning hull
        // shades gold at the deck to red at the tips instead of being one flat
        // colour with a wobble in it.
        rampOff: 0.06 + k * 0.12 + (1 - heat) * 0.3 + rand01(seed, index, 4) * 0.16,
        spin: 3,
        seed,
        index: index * 4 + k,
      })
    }
    // Every other pass also lifts smoke, so the column is thinner than the
    // flame under it rather than a matched pair of puffs.
    if ((index & 1) !== 0) return
    const smokeSize = (0.13 + rand01(seed, index, 6) * 0.15) * scale
    this.spawn({
      x: x + Math.cos(a) * spread * 0.6,
      y: ground + 0.75 * scale,
      z: z + Math.sin(a) * spread * 0.6,
      vx: 0.2,
      vy: 0.55,
      ay: 0.1,
      drag: 0.5,
      life: 1.8 + rand01(seed, index, 5) * 1.4,
      size: smokeSize,
      // Relative, not absolute. A burning building emits this every eleventh
      // tick for as long as it stands, so an absolute growth rate compounds over
      // seconds of life and stacks the battlefield with grey boulders — which is
      // exactly what the first pass at persistent damage smoke produced.
      grow: smokeSize * 0.3,
      tail: 0.8, // dissipate throughout; see the long plume's note above
      ramp: this.rampId('smoke'),
      rampOff: 0.2 + rand01(seed, index, 7) * 0.3,
      rampRate: 0.7,
      solid: true,
      shadowless: true,
      spin: 0.8,
      seed,
      index,
    })
  }

  // --- simulation ----------------------------------------------------------

  /**
   * Age and integrate everything by exactly one simulation tick.
   *
   * **Effect time is simulation time, not wall-clock time**, and that single
   * choice is the difference between an explosion and a pile.
   *
   * Events arrive on tick boundaries, so a layer that ages only when it draws
   * does no aging at all across a seek: `boot.seekTo` steps four hundred ticks
   * and then renders one frame with `dt = 0`. Every particle spawned by every
   * shot, impact and death in those four hundred ticks was therefore still
   * alive, still at spawn position, still at spawn size, all photographed on the
   * same frame — which is what the capture set showed, and what a critic
   * reasonably read as "a solid convex mass of same-sized billboards". The
   * distributions, ramps and ballistics were all being computed correctly and
   * then sampled at t = 0.
   *
   * Called from the renderer's `postFixedUpdate`, which runs during a headless
   * seek as well as in a live match, so a captured frame is an instant of a
   * battle rather than an accumulation of one.
   */
  fixedStep(): void {
    const dt = TICK_DT
    const p = this.pool
    for (let i = 0; i < this.capacity; i++) {
      if (!p.live[i]) continue
      p.life[i] -= dt
      if (p.life[i] <= 0) {
        p.live[i] = 0
        continue
      }
      const decay = Math.max(0, 1 - p.drag[i] * dt)
      p.vx[i] *= decay
      p.vz[i] *= decay
      p.vy[i] = p.vy[i] * decay + p.ay[i] * dt
      p.x[i] += p.vx[i] * dt
      p.y[i] += p.vy[i] * dt
      p.z[i] += p.vz[i] * dt
      p.size[i] = Math.max(0.01, p.size[i] + p.grow[i] * dt)
      p.rx[i] += p.sx[i] * dt
      p.ry[i] += p.sy[i] * dt
      p.rz[i] += p.sz[i] * dt

      // Landing. One weak bounce and then it lies where it fell, tumble and
      // all, until its life runs out — wreckage on the ground is what a fight
      // leaves behind, and a brick that keeps falling into the terrain leaves
      // nothing.
      const rest = p.rest[i]
      if (rest !== NO_REST) {
        // Sampled where the cube *is*, not where it was thrown from.
        const floor = (this.groundAt ? this.groundAt(p.x[i], p.z[i]) : 0) + rest
        if (p.y[i] < floor && p.vy[i] < 0) {
          p.y[i] = floor
          if (p.vy[i] < -1) {
            p.vy[i] *= -0.32
            p.vx[i] *= 0.5
            p.vz[i] *= 0.5
            p.sx[i] *= 0.4
            p.sy[i] *= 0.4
            p.sz[i] *= 0.4
          } else {
            p.vx[i] = 0
            p.vy[i] = 0
            p.vz[i] = 0
            p.ay[i] = 0
            p.sx[i] = 0
            p.sy[i] = 0
            p.sz[i] = 0
          }
        }
      }
    }
    this.ageBeams(dt)
  }

  /**
   * Rebuild the instance buffers.
   *
   * `alpha` is the sub-tick fraction, the same one the entity transforms
   * interpolate with. Every particle is offset along its own velocity and spun
   * on by its own angular velocity, so motion resolves at display rate even
   * though it is integrated at tick rate: a 144 Hz machine sees smooth arcs, and
   * it sees the *same* arcs a 30 Hz one does.
   */
  update(alpha: number): void {
    const sub = alpha * TICK_DT
    const p = this.pool
    let count = 0
    let solid = 0
    let plume = 0
    for (let i = 0; i < this.capacity; i++) {
      if (!p.live[i]) continue
      if (count + solid >= this.capacity) break

      const t = 1 - (p.life[i] - sub) / p.maxLife[i]
      // The dissolve. A cube holds its size until the tail, then collapses —
      // the blocky equivalent of an alpha fade, and the one that keeps every
      // pixel it draws on the palette.
      const tail = p.tail[i]
      const shrink = t <= 1 - tail ? 1 : Math.max(0, (1 - t) / Math.max(1e-4, tail))
      const s = Math.max(0.01, p.size[i] + p.grow[i] * sub) * shrink
      if (s <= 0.006) continue

      this.scratchPos.set(p.x[i] + p.vx[i] * sub, p.y[i] + p.vy[i] * sub, p.z[i] + p.vz[i] * sub)
      if (this.visibleAt && !this.visibleAt(this.scratchPos.x, this.scratchPos.z)) continue
      this.scratchEuler.set(
        p.rx[i] + p.sx[i] * sub,
        p.ry[i] + p.sy[i] * sub,
        p.rz[i] + p.sz[i] * sub,
      )
      this.scratchQuat.setFromEuler(this.scratchEuler)
      this.scratchScale.set(s, s * p.flat[i], s)
      this.scratchMatrix.compose(this.scratchPos, this.scratchQuat, this.scratchScale)

      const ramp = p.ramp[i]
      const tint = p.tint[i]
      if (ramp === 0) {
        this.scratchColor.setRGB(p.r[i] * tint, p.g[i] * tint, p.b[i] * tint)
      } else {
        const table = this.rampTable[ramp]
        const steps = table.length / 3
        const u = p.rampOff[i] + t * p.rampRate[i]
        let k = Math.floor(u * steps)
        if (k < 0) k = 0
        else if (k >= steps) k = steps - 1
        this.scratchColor.setRGB(table[k * 3] * tint, table[k * 3 + 1] * tint, table[k * 3 + 2] * tint)
      }

      if (p.solid[i]) {
        if (p.shadowless[i]) {
          this.plumeMesh.setMatrixAt(plume, this.scratchMatrix)
          this.plumeMesh.setColorAt(plume, this.scratchColor)
          plume++
        } else {
          this.solidMesh.setMatrixAt(solid, this.scratchMatrix)
          this.solidMesh.setColorAt(solid, this.scratchColor)
          solid++
        }
        continue
      }
      this.mesh.setMatrixAt(count, this.scratchMatrix)
      this.mesh.setColorAt(count, this.scratchColor)
      count++
    }
    this.mesh.count = count
    this.mesh.instanceMatrix.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true
    this.solidMesh.count = solid
    this.solidMesh.instanceMatrix.needsUpdate = true
    if (this.solidMesh.instanceColor) this.solidMesh.instanceColor.needsUpdate = true
    this.plumeMesh.count = plume
    this.plumeMesh.instanceMatrix.needsUpdate = true
    if (this.plumeMesh.instanceColor) this.plumeMesh.instanceColor.needsUpdate = true
    this.writeBeams(sub)
  }

  get liveCount(): number {
    return this.mesh.count + this.solidMesh.count + this.plumeMesh.count
  }

  attachFog(fog: Fog): void {
    for (const mesh of [this.mesh, this.solidMesh, this.plumeMesh, this.beamMesh]) {
      fog.attach(mesh.material as Material, false, true)
    }
  }

  dispose(): void {
    this.mesh.geometry.dispose()
    ;(this.mesh.material as MeshBasicMaterial).dispose()
    this.mesh.dispose()
    this.solidMesh.geometry.dispose()
    ;(this.solidMesh.material as MeshLambertMaterial).dispose()
    this.solidMesh.dispose()
    this.plumeMesh.geometry.dispose()
    ;(this.plumeMesh.material as MeshLambertMaterial).dispose()
    this.plumeMesh.dispose()
    this.beamMesh.geometry.dispose()
    ;(this.beamMesh.material as MeshBasicMaterial).dispose()
    this.beamMesh.dispose()
  }

  /**
   * The body colour of a beam, taken from the same ramp its core cubes use.
   *
   * Read out of the built ramp table rather than composed here, because every
   * entry in that table has already been snapped to a real palette colour. The
   * old path asked `accent()` for the weapon's ramp by name and a *bullet* names
   * `fire`, which is not an accent — so it fell through to a hardcoded
   * `[1, 0.7, 0.3]` that belongs to no palette, and every tracer in the game was
   * drawn in it.
   */
  private beamRgb(colour: string): void {
    const energy = colour !== 'fire'
    const table = this.rampTable[this.rampId(energy ? `energy:${colour}` : 'fire')]
    // Gold for a shell, the accent proper for an energy weapon: one step in from
    // white in both cases, so the core cubes read as the hot middle of it.
    const k = energy ? 2 : 4
    // Into a scratch triple rather than out as a tuple. This runs on the
    // `weapon:fire` path, which in a firing line is several times a frame, and
    // "allocate nothing per frame" is a rule about the steady state rather than
    // about `update()` specifically.
    this.beamTint[0] = table[k * 3]
    this.beamTint[1] = table[k * 3 + 1]
    this.beamTint[2] = table[k * 3 + 2]
  }

  private accent(ramp: string, step: number): [number, number, number] {
    const entry = this.palette.accentRamps?.[ramp] ?? this.palette.terrainRamps[ramp]
    const indices = entry?.indices
    if (!indices) return [1, 0.7, 0.3]
    const [r, g, b] = this.palette.rgb(indices[Math.min(step, indices.length - 1)])
    return [r / 255, g / 255, b / 255]
  }

  /** Small plumes behind moving tracks, sharing the bounded effect pool. */
  movement(x: number, z: number, ground: number, heading: number, seed: number): void {
    const dx = Math.cos(heading)
    const dz = Math.sin(heading)
    for (const side of [-1, 1]) {
      this.spawn({
        x: x - dx * 0.55 + dz * side * 0.38,
        y: ground + 0.06,
        z: z - dz * 0.55 - dx * side * 0.38,
        vx: -dx * 0.25 + dz * side * 0.14,
        vz: -dz * 0.25 - dx * side * 0.14,
        vy: 0.18,
        drag: 1.5,
        life: 0.65,
        size: 0.08 + rand01(seed, side, 4) * 0.08,
        grow: 0.13,
        tail: 0.7,
        ramp: this.rampId('dust'),
        rampOff: 0.35,
        solid: true,
        shadowless: true,
        spin: 0.5,
        seed,
        index: side,
      })
    }
  }

  /** Claim a slot, overwriting the oldest if the pool is full. */
  private spawn(spec: {
    x: number
    y: number
    z: number
    vx?: number
    vy?: number
    vz?: number
    ay?: number
    drag?: number
    /** World Y to land on. Omitted for anything that should pass through it. */
    rest?: number
    life: number
    size: number
    grow?: number
    tail?: number
    /** Squash the box on Y to this fraction of its size. Ground marks only. */
    flat?: number
    r?: number
    g?: number
    b?: number
    /** Per-particle brightness in [0, 1]. See the pool field. */
    tint?: number
    ramp?: number
    rampOff?: number
    rampRate?: number
    solid?: boolean
    /** Solid, but casts no shadow. Set on smoke; see the pool field. */
    shadowless?: boolean
    /** Peak angular velocity, radians per second. Zero pins the orientation. */
    spin?: number
    /** Fixed yaw, for anything that must lie flat rather than tumble. */
    yaw?: number
    seed: number
    index: number
  }): void {
    const i = this.next
    this.next = (this.next + 1) % this.capacity
    const p = this.pool
    const seed = spec.seed
    const k = spec.index
    p.solid[i] = spec.solid ? 1 : 0
    p.shadowless[i] = spec.shadowless ? 1 : 0
    p.x[i] = spec.x
    p.y[i] = spec.y
    p.z[i] = spec.z
    p.vx[i] = spec.vx ?? 0
    p.vy[i] = spec.vy ?? 0
    p.vz[i] = spec.vz ?? 0
    p.ay[i] = spec.ay ?? 0
    p.drag[i] = spec.drag ?? 0
    p.rest[i] = spec.rest ?? NO_REST
    p.life[i] = spec.life
    p.maxLife[i] = spec.life
    p.size[i] = spec.size
    p.grow[i] = spec.grow ?? 0
    p.tail[i] = spec.tail ?? 0.4
    p.flat[i] = spec.flat ?? 1
    p.r[i] = spec.r ?? 1
    p.g[i] = spec.g ?? 1
    p.b[i] = spec.b ?? 1
    p.tint[i] = spec.tint ?? 1
    p.ramp[i] = spec.ramp ?? 0
    p.rampOff[i] = spec.rampOff ?? 0
    p.rampRate[i] = spec.rampRate ?? RAMP_RATE
    p.live[i] = 1

    // Random orientation and tumble. A cube with no rotation is a screen-aligned
    // square and reads as a billboard, which is exactly the thing the reference
    // does not look like; the tumble is what makes a burst read as solid matter.
    const spin = spec.spin ?? 0
    if (spec.yaw !== undefined) {
      p.rx[i] = 0
      p.ry[i] = spec.yaw
      p.rz[i] = 0
      p.sx[i] = 0
      p.sy[i] = 0
      p.sz[i] = 0
      return
    }
    p.rx[i] = rand01(seed, k, 20) * TAU
    p.ry[i] = rand01(seed, k, 21) * TAU
    p.rz[i] = rand01(seed, k, 22) * TAU
    p.sx[i] = (rand01(seed, k, 23) - 0.5) * 2 * spin
    p.sy[i] = (rand01(seed, k, 24) - 0.5) * 2 * spin
    p.sz[i] = (rand01(seed, k, 25) - 0.5) * 2 * spin
  }
}

/**
 * A stable seed for a world position.
 *
 * Effects must not consult `ctx.rng` — they run per client at a per-machine
 * particle budget, so a draw from the shared stream would desync — and they must
 * not use `Math.random` either, because `npm run shots` captures are
 * bit-reproducible by contract. Deriving the noise from the burst's own
 * fixed-point position gives both: identical everywhere, and different for every
 * blast.
 */
/**
 * A unit box with both end caps pulled in towards its axis.
 *
 * A beam drawn as a plain box is a bar of constant width that starts and stops
 * dead, which is the single thing that makes ours read as a drawn line and the
 * reference's read as light: energy tapers into its source and out of its
 * target. Pinching the `z = ±0.5` vertices to a fifth of the width turns the box
 * into a faceted spindle — still eight flat quads, still unmistakably blocky, but
 * thickest in the middle and nearly closed at both tips.
 *
 * Winding is untouched (the vertices move along their own face, they do not
 * reorder), which matters because §9 derives the normal from it.
 */
function taperedPrism(tip = 0.2): BoxGeometry {
  // `depthSegments = 2` is load-bearing, and its absence is why every beam in
  // the game drew about two pixels wide however wide it asked to be. A default
  // `BoxGeometry` has vertices at `z = ±0.5` and *nowhere else*, so the pinch
  // below caught all twenty-four of them and the "taper" was a uniform 5×
  // narrowing of the whole box. A middle ring at `z = 0` is what the pinch
  // tapers away *from*: the beam is now its nominal width across the middle and
  // closes to a tip at each end, which is the shape the constant was tuned for.
  const geometry = new BoxGeometry(1, 1, 1, 1, 1, 2)
  const pos = geometry.attributes.position
  for (let i = 0; i < pos.count; i++) {
    if (Math.abs(pos.getZ(i)) < 0.499) continue
    pos.setX(i, pos.getX(i) * tip)
    pos.setY(i, pos.getY(i) * tip)
  }
  pos.needsUpdate = true
  // The same baked per-face brightness the flame cubes carry, and for the same
  // reason: a beam drawn at one flat value across every face is a *shape* laid
  // on the frame rather than a round volume travelling down it, which is half of
  // what "a flat line with no core in it" was describing. Three values across
  // the sleeve give it a lit side and a shaded one without giving it a colour.
  bakeFaceShade(geometry)
  return geometry
}

/**
 * Write a per-face brightness scalar into a box's colour attribute.
 *
 * A multiply by one scalar on all three channels keeps the palette *direction*
 * the colour-discipline gate measures — it is "this palette entry under some
 * amount of light", never a new hue. The weights follow `SUN_DIRECTION` so an
 * effect cube agrees with the hull burning under it about where the light is,
 * and they never fall below 0.84, because this is a fake shade on something that
 * is emitting rather than a real one on something that is not.
 *
 * The band was 0.7–1.0 and it was too wide. Two of a cube's three visible faces
 * rendered at three quarters of the palette entry they were handed — the same
 * ratio the scene's own sun puts on a shaded hull — so a fireball read as
 * orange-*painted* rubble lit from the same direction as the tanks under it
 * rather than as the thing throwing the light. Compressed to 0.84–1.0 each cube
 * still shows three distinct values, which is the whole reason this attribute
 * exists, while its darkest face stays plainly hotter than any lit surface
 * beside it.
 */
function bakeFaceShade(geometry: BoxGeometry): void {
  const normal = geometry.attributes.normal
  const colors = new Float32Array(normal.count * 3)
  for (let i = 0; i < normal.count; i++) {
    const nx = normal.getX(i)
    const ny = normal.getY(i)
    const nz = normal.getZ(i)
    const v = ny > 0.5 ? 1 : ny < -0.5 ? 0.84 : nx > 0.5 ? 0.96 : nz < -0.5 ? 0.93 : nz > 0.5 ? 0.89 : 0.86
    colors[i * 3] = v
    colors[i * 3 + 1] = v
    colors[i * 3 + 2] = v
  }
  geometry.setAttribute('color', new BufferAttribute(colors, 3))
}

/**
 * A unit box carrying a baked per-face brightness in its colour attribute.
 *
 * The unlit pass draws fire, and fire is light, so it cannot be handed to the
 * scene lights — but a cube with one value per face and no shading between them
 * is a flat hexagon, and overlapping flat hexagons of the same ramp step are one
 * polygon with a hard edge. See `bakeFaceShade` for the weights.
 */
function shadedBox(): BoxGeometry {
  const geometry = new BoxGeometry(1, 1, 1)
  bakeFaceShade(geometry)
  return geometry
}

function seedOf(x: number, z: number): number {
  return (Math.imul((x * 4096) | 0, 0x9e3779b1) ^ Math.imul((z * 4096) | 0, 0x85ebca6b)) >>> 0
}

/** A stable number in [0, 1) for a seed, a particle index and a channel. */
function rand01(seed: number, index: number, salt: number): number {
  let h = (seed ^ Math.imul(index + 1, 0x27d4eb2f) ^ Math.imul(salt + 1, 0x165667b1)) >>> 0
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39) >>> 0
  h ^= h >>> 15
  return (h >>> 0) / 4294967296
}

function clampInt(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}
